import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { getFirestore, collection, getDocs, query, where, getDoc, doc, onSnapshot } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { 
  FaFilter, FaDownload, FaLightbulb, FaChevronDown, FaTimes, 
  FaInfoCircle, FaExclamationTriangle, FaCalculator, FaStar 
} from "react-icons/fa";
import TradingCalendar from '../components/TradingCalendar';
import { getTodayPreTradeChecklist, getUserSettings, getSettings } from "../services/FirestoreService";
import { isThisWeek, isToday, parseISO } from "date-fns";
import SimpleCalculator from '../components/SimpleCalculator'; 
import { db } from "../firebase";

// IMPORT THE NEWLY CREATED AiFeedbackModule HERE
import AiFeedbackModule from '../components/AiFeedback';

import {
  RadialBarChart,
  RadialBar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  BarChart,
  ReferenceLine,
  Legend,
  Bar,
  LabelList,
} from "recharts";
import { format,  parse, startOfWeek, endOfWeek, isSameDay, startOfDay, endOfDay } from "date-fns";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// New helper to parse AM/PM time into a standard 24-hour format
function parseTimeTo24h(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const lowerTime = timeStr.toLowerCase();
    let [h, m] = [NaN, NaN];

    if (lowerTime.includes('am') || lowerTime.includes('pm')) {
        const [time, period] = lowerTime.split(' ');
        [h, m] = time.split(':').map(Number);
        if (period === 'pm' && h < 12) h += 12;
        if (period === 'am' && h === 12) h = 0; // Midnight case
    } else {
        [h, m] = timeStr.split(':').map(Number);
    }

    if (isNaN(h) || isNaN(m)) return null;
    return [h, m];
}

function checkTimeRange(rowTime, fromTime, toTime) {
  if (!rowTime && (fromTime || toTime)) return false; 
  if (!fromTime && !toTime) return true;
  if (fromTime && !toTime) return rowTime >= fromTime;
  if (!fromTime && toTime) return rowTime <= toTime;
  
  if (fromTime > toTime) {
    // Range spans midnight (e.g., 19:00 to 03:00)
    return rowTime >= fromTime || rowTime <= toTime;
  } else {
    // Normal range
    return rowTime >= fromTime && rowTime <= toTime;
  }
}

// New main function to get or recalculate duration
function getAndRecalculateDuration(row) {
    const existingDuration = row["Duration (h:mm)"] || row["Duration"];
    
    // Check if a valid, non-zero duration already exists
    const totalSecondsInExisting = existingDuration ? (existingDuration.split(':').map(Number).reduce((acc, val, i, arr) => acc + val * Math.pow(60, arr.length - 1 - i), 0)) : 0;
    if (existingDuration && totalSecondsInExisting > 0) {
        return existingDuration;
    }

    // If not, try to recalculate from entry and exit times
    const date = parseDateVal(row["Date"]);
    const entryTimeParts = parseTimeTo24h(row["Entry Time"]);
    const exitTimeParts = parseTimeTo24h(row["Exit Time"]);

    if (!date || !entryTimeParts || !exitTimeParts) {
        return existingDuration || "0:0:0";
    }

    try {
        const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), entryTimeParts[0], entryTimeParts[1]);
        const exitDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), exitTimeParts[0], exitTimeParts[1]);
        
        let diffInMillis = exitDate.getTime() - entryDate.getTime();
        if (diffInMillis < 0) {
            diffInMillis += 24 * 60 * 60 * 1000; // Assume overnight trade
        }

        const hours = Math.floor(diffInMillis / 3600000);
        const minutes = Math.floor((diffInMillis % 3600000) / 60000);
        const seconds = Math.floor((diffInMillis % 60000) / 1000);
        
        return `${hours}:${minutes}:${seconds}`;
    } catch (e) {
        return existingDuration || "0:0:0"; // Fallback on error
    }
}

// Collapsible, zero-dependency wrapper
function CollapsibleSection({ title, defaultOpen = true, id, children }) {
  return (
    <details
      id={id}
      open={defaultOpen}
      className="group mb-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm"
    >
      <summary className="flex cursor-pointer select-none items-center justify-between gap-3 px-4 py-3">
        <span className="text-base font-semibold text-blue-900 dark:text-blue-400">
          {title}
        </span>
        <FaChevronDown className="text-gray-500 dark:text-gray-400 transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <div className="px-4 pb-4">{children}</div>
    </details>
  );
}


function getProductShort(val) {
  return val ? val.split(" - ")[0] : "";
}
function isFundingRow(row) {
  const type = String(row?.["Funding Type"] || "").toLowerCase();
  return row?.isFunding === true
    || type === "deposit"
    || type === "withdrawal"
    || getProductShort(row?.["Product"]) === "Funding";
}

function isDidNotTake(val) {
  const s = String(val ?? "").trim().toLowerCase();
  return s === "did not take" || s === "dnt";
}

function isBuyPos(val) {
  const p = String(val || "").trim().toLowerCase();
  return p === "buy" || p === "countertrend - buy" || p === "long";
}

function isSellPos(val) {
  const p = String(val || "").trim().toLowerCase();
  return p === "sell" || p === "countertrend - sell" || p === "short";
}

function formatCurrency(val) {
  if (!val || isNaN(val)) return "$0.00";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}
function formatPercent(val) {
  if (val == null || isNaN(val)) return "0%";
  const f = Number(val).toFixed(2);
  return f.endsWith(".00") ? `${Number(val).toFixed(0)}%` : `${f}%`;
}
function formatDuration(val) {
  if (!val) return "--"; 
  
  const parts = val.split(":").map(Number);
  let h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;

  if (h >= 24) {
    const days = Math.floor(h / 24);
    const remHours = h % 24;
    return `${days} day${days !== 1 ? 's' : ''} ${remHours} hour${remHours !== 1 ? 's' : ''}`;
  }

  const displayParts = [];
  if (h > 0) displayParts.push(`${h} hour${h > 1 ? 's' : ''}`);
  if (m > 0) displayParts.push(`${m} min${m > 1 ? 's' : ''}`);
  if (s > 0 && h === 0) displayParts.push(`${s} sec${s > 1 ? 's' : ''}`);

  if (displayParts.length > 0) {
    return displayParts.join(' ');
  }
  
  return "< 1 min";
}


function shortProduct(val) {
  return val?.split(" - ")[0] || "";
}

function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}
function formatDateUS(dateVal) {
  try {
    const d = parseDateVal(dateVal);
    return isValidDate(d) ? format(d, "MM/dd/yyyy") : String(dateVal ?? "");
  } catch {
    return String(dateVal ?? "");
  }
}
function parseNum(val) {
  if (!val) return 0;
  if (typeof val === "number") return val;
  return Number(String(val).replace(/[^\d.-]/g, "")) || 0;
}

async function fetchJournalRows(userId, accountId) {
  const db = getFirestore();
  const journalRef = collection(db, "users", userId, "accounts", accountId, "journal");
  const snapshot = await getDocs(journalRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  if (isNaN(h) || isNaN(parseInt(m, 10))) return "";
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}

function prettyDuration(raw) {
  if (!raw) return "";
  
  const parts = String(raw).split(":").map(Number);
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;

  const displayParts = [];
  if (h > 0) displayParts.push(`${h} hr${h > 1 ? 's' : ''}`);
  if (m > 0) displayParts.push(`${m} min${m > 1 ? 's' : ''}`);
  
  if (s > 0 && h === 0) {
    displayParts.push(`${s} sec${s > 1 ? 's' : ''}`);
  }

  if (displayParts.length > 0) {
    return displayParts.join(' ');
  }
  
  return "< 1 min";
}



function getDurationMins2(str) {
  if (!str) return 0;
  const [h, m] = str.split(":").map(Number);
  return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
}

function parseDateVal(val) {
  if (!val) return new Date(NaN);
  if (val && typeof val.toDate === "function") return val.toDate();        
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val;     

  const s = String(val).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00`);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return new Date(s);

  if (s.includes("/")) {
    const formats = ["MM/dd/yyyy", "M/d/yyyy", "MM/dd/yy", "M/d/yy"];
    for (const f of formats) {
      const d = parse(s, f, new Date());
      if (isValidDate(d)) return d;
    }
  }

  const d = new Date(s);
  return d;
}

const toKeyYMD = (v) => {
  const d = parseDateVal(v);
  return isValidDate(d) ? format(d, "yyyy-MM-dd") : null;
};



const BLANK = "__BLANK__";

function canonicalizeTimeframe(tfRaw) {
  if (!tfRaw) return null;
  const s = String(tfRaw).trim().toLowerCase();

  if (s === "daily") return "1d";
  if (/^\d+$/.test(s)) return `${Number(s)}m`; 

  const m = s.match(/^(\d+)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)$/i);
  
  if (!m) return s; 
  
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  
  if (unit.startsWith("s")) return `${n}s`;
  if (unit.startsWith("m")) return `${n}m`;
  if (unit.startsWith("h")) return `${n}h`;
  if (unit.startsWith("d")) return `${n}d`;
  return s;
}

function timeframeToMinutes(tf) {
  if (!tf) return null;
  
  const m = String(tf).toLowerCase().match(/^(\d+)\s*([mhds])$/);
  
  if (!m) return null;
  
  const n = Number(m[1]);
  const u = m[2];
  
  if (u === "s") return n / 60; 
  if (u === "m") return n;
  if (u === "h") return n * 60;
  if (u === "d") return n * 1440;
  return null;
}

const ChartInfoTooltip = ({ text }) => (
  <div className="relative inline-block ml-2 group z-10">
    <FaInfoCircle className="text-gray-400 hover:text-blue-500 cursor-help text-sm" />
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-pre-wrap text-left">
      {text}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
    </div>
  </div>
);


export default function Stats({ userId, accountId }) {
  const [journalRows, setJournalRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const contentRef = useRef(null); 
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); 
  const [pdfProgress, setPdfProgress] = useState(0);  
  const POS = "#2563eb";  
  const NEG = "#FFFF00";  
  const [selectedDate, setSelectedDate] = useState(null);
  const [subStatus, setSubStatus] = useState("");
  const [userPrefs, setUserPrefs] = useState({});
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [showCalculator, setShowCalculator] = useState(false);

  const cardBase = `
  relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 
  bg-white dark:bg-gray-800 shadow-sm p-6 
  flex flex-col items-center text-center 
  hover:shadow-md transition-all duration-300
`;
  const cardHeader = "font-semibold text-gray-800 dark:text-gray-300 mb-2";
  const Accent = () => (
  <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-80" />
);

  const [acctSettings, setAcctSettings] = useState(null);

  // Helper to force Recharts legend text to be readable in both light and dark mode
  const renderLegendText = (value) => {
    return <span className="text-gray-700 dark:text-gray-300 font-medium">{value}</span>;
  };

  const isBacktesting = useMemo(() => {
    return (
      acctSettings?.isBacktest === true ||
      String(acctSettings?.tag || "").toLowerCase().includes("backtest") ||
      String(acctSettings?.fundingType || "").toLowerCase().includes("backtest")
    );
  }, [acctSettings]);

  useEffect(() => {
    if (!userId) return;
    const prefsRef = doc(db, "users", userId, "settings", "prefs");
    const unsub = onSnapshot(prefsRef, (docSnap) => {
      if (docSnap.exists()) setUserPrefs(docSnap.data());
    });
    return () => unsub();
  }, [userId]);
  
  useEffect(() => {
    if (!userId || !accountId) return;
    getSettings(userId, accountId)
      .then(setAcctSettings)
      .catch(() => setAcctSettings({})); 
  }, [userId, accountId]);

  useEffect(() => {
    if (!userId) return;

    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const subData = snapshot.docs[0].data();
        setSubStatus(subData.status || "");
      } else {
        setSubStatus(""); 
      }
    });

    return () => unsubscribe();
  }, [userId]);

  const isTrialing = String(subStatus).toUpperCase() === "TRIALING";

  const [filters, setFilters] = useState({
  dateFilter: "all",
  customFrom: "",
  customTo: "",
  productFilter: "",
  resultFilter: "",
  rulesFilter: "",
  positionFilter: "",
  entryStrategyFilter: "",
  exitStrategyFilter: "",
  sessionFilter: "",   
  entryTimeFrom: "",   
  entryTimeTo: "", 
  dayOfWeekFilter: "",    
});

  const filterOptions = useMemo(() => {
  const options = {
    products: new Set(),
    rules: new Set(),
    positions: new Set(),
    entryStrategies: new Set(),
    exitStrategies: new Set(),
    sessions: new Set(), // Added
  };

    journalRows.forEach(row => {
        if (row["Product"] && !isFundingRow(row)) {
          options.products.add(getProductShort(row["Product"]));
        }
      if (row["Did I follow my Rules?"]) options.rules.add(row["Did I follow my Rules?"]);
      if (row["Position"]) options.positions.add(row["Position"]);
      if (row["Entry Strategy"]) options.entryStrategies.add(row["Entry Strategy"]);
      if (row["Exit Strategy"]) options.exitStrategies.add(row["Exit Strategy"]);
      if (row["Session"]) options.sessions.add(row["Session"]);
    });

    return {
      products: Array.from(options.products).sort(),
      rules: Array.from(options.rules).sort(),
      positions: Array.from(options.positions).sort(),
      entryStrategies: Array.from(options.entryStrategies).sort(),
      exitStrategies: Array.from(options.exitStrategies).sort(),
      sessions: Array.from(options.sessions).sort(), // Added
    };
  }, [journalRows]);

  const filteredRows = useMemo(() => {
      let rows = journalRows;
      const {
        dateFilter,
        customFrom,
        customTo,
        productFilter,
        resultFilter,
        rulesFilter,
        positionFilter,
        entryStrategyFilter,
        exitStrategyFilter,
        sessionFilter,    // Added
        entryTimeFrom,    // Added
        entryTimeTo,
        dayOfWeekFilter,
      } = filters;

      if (dateFilter === "today") {
        rows = rows.filter((r) => isToday(parseDateVal(r["Date"])));
      } else if (dateFilter === "week") {
        rows = rows.filter((r) => isThisWeek(parseDateVal(r["Date"]), { weekStartsOn: 1 }));
      } else if (dateFilter === "custom") {
        const from = customFrom ? new Date(customFrom) : null;
        const to = customTo ? new Date(customTo) : null;
        rows = rows.filter((r) => {
          const d = parseDateVal(r["Date"]);
          if (from && d < from) return false;
          if (to && d > to) return false;
          return true;
        });
      }
      if (productFilter) {
        rows = rows.filter((r) => shortProduct(r["Product"]) === productFilter);
      }
      if (sessionFilter) {
    rows = rows.filter((r) => {
      const val = (r["Session"] ?? "").toString().trim();
      return sessionFilter === BLANK ? val === "" : val.toLowerCase() === sessionFilter.toLowerCase();
    });
  }

        // 2. Entry Time Range Filter
        if (entryTimeFrom || entryTimeTo) {
          rows = rows.filter((r) => {
            const parsed = parseTimeTo24h(r["Entry Time"]);
            if (!parsed) return false;
            const rowTime = `${String(parsed[0]).padStart(2, '0')}:${String(parsed[1]).padStart(2, '0')}`;
            return checkTimeRange(rowTime, entryTimeFrom, entryTimeTo);
          });
        }
      if (resultFilter) {
        rows = rows.filter((r) => {
          if (!r["Win"] && !r["Loss"]) return false;
          const win = r["Win"] === "TRUE" || r["Win"] === true || r["Win"] === "Yes";
          if (resultFilter === "Won") return win;
          if (resultFilter === "Loss") return !win;
          return true;
        });
      }
      if (rulesFilter) {
        rows = rows.filter((r) => {
          const val = (r["Did I follow my Rules?"] ?? "").trim();
          return rulesFilter === BLANK ? val === "" : val.toLowerCase() === rulesFilter.toLowerCase();
        });
      }
      if (positionFilter) {
        rows = rows.filter((r) => {
          const positionMatch = (r["Position"] || "").toLowerCase() === positionFilter.toLowerCase();
          return positionMatch;
        });
      }
      if (entryStrategyFilter) {
        rows = rows.filter((r) => {
          const val = (r["Entry Strategy"] ?? "").trim();
          return entryStrategyFilter === BLANK ? val === "" : val.toLowerCase() === entryStrategyFilter.toLowerCase();
        });
      }
      if (exitStrategyFilter) {
        rows = rows.filter((r) => {
          const val = (r["Exit Strategy"] ?? "").trim();
          return exitStrategyFilter === BLANK ? val === "" : val.toLowerCase() === exitStrategyFilter.toLowerCase();
        });
      }
      if (dayOfWeekFilter) {
        rows = rows.filter((r) => {
          const d = parseDateVal(r["Date"]);
          if (!isValidDate(d)) return false;
          return format(d, "EEEE") === dayOfWeekFilter;
        });
      }

      rows = rows.filter(r => !isFundingRow(r));
      rows = rows.filter(r => !isDidNotTake(r["Position"]));

      rows.sort((a, b) => {
        const dateB = parseDateVal(b.Date);
        const dateA = parseDateVal(a.Date);
        if (dateA < dateB) return 1;
        if (dateA > dateB) return -1;

        const timeB = parseTime(b['Entry Time']);
        const timeA = parseTime(a['Entry Time']);
        return timeB - timeA;
      });

      return rows;
    }, [journalRows, filters]);

// --- CUSTOM CHART INDICATORS ---

  // Indicator for Ideal vs Actual Stop Loss
  const renderStopLossIndicator = (actualKey, idealKey, data) => (props) => {
    const { x, y, width, index } = props;
    const row = data[index];
    if (!row) return null;

    const actual = row[actualKey] || 0;
    const ideal = row[idealKey] || 0;

    if (actual === 0 || ideal === 0) return null;

    // Ideal is higher than Actual
    if (ideal > actual) {
      return (
        <text x={x + width / 2} y={y - 5} fill="#ef4444" fontSize="16" fontWeight="bold" textAnchor="middle">
          ↑
        </text>
      );
    }
    // Ideal is less than Actual
    if (ideal < actual) {
      return (
        <text x={x + width / 2} y={y - 5} fill="#22c55e" fontSize="16" fontWeight="bold" textAnchor="middle">
          ↓
        </text>
      );
    }
    return null;
  };

  // Indicator for Potential vs Actual Ticks/Pips
  const renderPotentialIndicator = (actualKey, potentialKey, data, isLoss = false) => (props) => {
    const { x, y, width, index } = props;
    const row = data[index];
    if (!row) return null;

    const actual = Math.abs(row[actualKey] || 0);
    const potential = Math.abs(row[potentialKey] || 0);

    // If potential is at least 20% higher than actual
    if (potential > actual * 1.2 && actual > 0) {
      const iconSize = 12;
      // Center the icon horizontally over the bar
      const iconX = x + (width / 2) - (iconSize / 2);
      // Position it just above the top of the bar
      const iconY = y - iconSize - 4; 

      if (isLoss) {
        return (
          <FaExclamationTriangle x={iconX} y={iconY} fill="#ef4444" size={iconSize} />
        );
      } else {
        return (
          <FaStar x={iconX} y={iconY} fill="#f59e0b" size={iconSize} />
        );
      }
    }
    return null;
  };

// Indicator for Negative Expectancy (Avg Loss > Avg Win)
  const renderRRErrorIndicator = (winKey, lossKey, data) => (props) => {
    const { x, y, height, index } = props;
    const row = data[index];
    if (!row) return null;

    const avgWin = Math.abs(row[winKey] || 0);
    const avgLoss = Math.abs(row[lossKey] || 0);

    // If average loss is bigger than average win
    if (avgLoss > avgWin && avgLoss > 0) {
      // For negative bars, 'x' is the leftmost edge. 
      // We subtract 18 to place the icon to the left of the bar without overlapping.
      return (
        <FaExclamationTriangle 
          x={x - 18} 
          y={y + (height / 2) - 6} 
          fill="#ef4444" 
          size={12} 
        />
      );
    }
    return null;
  };

  // Indicator to show Win % at the end of pre-calculated percentage bars
  const renderWinPctValueLabel = (winKey, lossKey, data) => (props) => {
    const { x, y, width, height, index } = props;
    const row = data[index];
    if (!row) return null;

    const wins = row[winKey] || 0;
    const losses = row[lossKey] || 0;

    // Hide the label if there is no data for this bucket
    if (wins === 0 && losses === 0) return null;

    return (
      <text 
        x={x + width + 8} 
        y={y + (height / 2) + 4} 
        fill={isDark ? "#94a3b8" : "#6b7280"} 
        fontSize="12" 
        fontWeight="bold"
      >
        {wins}%
      </text>
    );
  };

  // Indicator to show Total Count at the end of stacked raw count bars
  const renderTotalCountLabel = (winKey, lossKey, data) => (props) => {
    const { x, y, width, height, index } = props;
    const row = data[index];
    if (!row) return null;

    const wins = row[winKey] || 0;
    const losses = row[lossKey] || 0;
    const total = wins + losses;

    if (total === 0) return null;

    return (
      <text 
        x={x + width + 8} 
        y={y + (height / 2) + 4} 
        fill={isDark ? "#94a3b8" : "#6b7280"} 
        fontSize="12" 
        fontWeight="bold"
      >
        {total}
      </text>
    );
  };

const calendarRowsSafe = useMemo(() => {
  return (filteredRows || []) // Changed to filteredRows
    .map((r) => {
      const ymd = toKeyYMD(r["Date"]);
      return ymd ? { ...r, Date: ymd } : null; 
    })
    .filter(Boolean);
}, [filteredRows]); // Updated dependency

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
  setCurrentPage(1);
}, [filters]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));

  const currentRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, currentPage, itemsPerPage]);


  useEffect(() => {
    let unsub;
    let active = true;

    async function loadJournalRows(currentUserId, currentAccountId) {
      setLoading(true);
      try {
        if (currentUserId && currentAccountId) {
          const rows = await fetchJournalRows(currentUserId, currentAccountId);
          if (active) setJournalRows(rows);
        } else if (currentUserId) {
          if (active) setJournalRows([]);
        } else {
          if (active) setJournalRows([]);
        }
      } catch (e) {
        if (active) setJournalRows([]);
      }
      if (active) setLoading(false);
    }

    if (userId && accountId) {
      loadJournalRows(userId, accountId);
      return () => { active = false; };
    } else {
      const auth = getAuth();
      unsub = onAuthStateChanged(auth, (user) => {
        if (user && accountId) {
          loadJournalRows(user.uid, accountId);
        } else {
          setJournalRows([]);
          setLoading(false);
        }
      });
      return () => {
        active = false;
        if (unsub) unsub();
      };
    }
  }, [userId, accountId]);

  function pctOpenToClose(startBal, inc) {
    const s = Number(startBal);
    const d = Number(inc);
    if (!Number.isFinite(s) || !Number.isFinite(d) || s === 0) return 0;

    const e = s + d; 
    const sameSign = (s >= 0 && e >= 0) || (s <= 0 && e <= 0);

    if (sameSign) return (d / Math.abs(s)) * 100;
    return (2 * d / (Math.abs(s) + Math.abs(e))) * 100;
  }


  const handleDayClick = useCallback((date) => {
      if (date) {
        const parsed = parseDateVal(date);
        if (isValidDate(parsed)) {
          setSelectedDate(startOfDay(parsed)); 
        }
      }
    }, []);


  const tradeCount = filteredRows.length;
  const tradingDaysCount = useMemo(() => {
    const s = new Set();
    (filteredRows || []).forEach((r) => {
      const key = toKeyYMD(r["Date"]);
      if (key) s.add(key);
    });
    return s.size;
  }, [filteredRows]);
  
  const winCount = filteredRows.filter(
    (r) => r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true
  ).length;
  const lossCount = filteredRows.filter(
    (r) => r["Loss"] === "TRUE" || r["Loss"] === "Yes" || r["Loss"] === true
  ).length;
  const winPct = tradeCount ? Math.round((winCount / tradeCount) * 100) : 0;

const accountBalanceData = useMemo(() => {
    if (!filteredRows?.length) return [];

    // Sort strictly chronologically
    const rowsAsc = [...filteredRows].sort((a, b) => {
      const timeA = parseDateVal(a["Date"]).getTime() || 0;
      const timeB = parseDateVal(b["Date"]).getTime() || 0;
      if (timeA !== timeB) return timeA - timeB;

      const numA = parseNum(a["Trade #"]);
      const numB = parseNum(b["Trade #"]);
      if (numA && numB && numA !== numB) return numA - numB;

      return 0;
    });

    // 1. Calculate the time span between the first and last trade
    const firstDate = parseDateVal(rowsAsc[0]["Date"]);
    const lastDate = parseDateVal(rowsAsc[rowsAsc.length - 1]["Date"]);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // 2. Toggle flag: if more than 31 days, group by week
    const useWeekly = diffDays > 31;

    const dataPoints = [];
    let currentPeriod = "";

    rowsAsc.forEach((row) => {
      const dateVal = parseDateVal(row["Date"]);
      if (!isValidDate(dateVal)) return;
      
      let periodKey = "";
      let displayDate = "";

      // 3. Apply the appropriate grouping logic based on the span
      if (useWeekly) {
          const weekStart = startOfWeek(dateVal, { weekStartsOn: 1 });
          periodKey = format(weekStart, "yyyy-MM-dd");
          displayDate = format(weekStart, "MM/dd/yyyy");
      } else {
          periodKey = format(dateVal, "yyyy-MM-dd");
          displayDate = format(dateVal, "MM/dd/yyyy");
      }
      
      const currentBalance = parseNum(row["Actual Balance"]);

      // 4. Push or update the data point
      if (periodKey !== currentPeriod) {
          currentPeriod = periodKey;
          dataPoints.push({
              dateKey: periodKey,
              date: displayDate,
              balance: currentBalance
          });
      } else {
          // Overwrite with the latest trade's balance for this period
          dataPoints[dataPoints.length - 1].balance = currentBalance;
      }
    });

    return dataPoints;
  }, [filteredRows]);

  // NEW: Session Chart Data Logic
  const winsLossBySession = useMemo(() => {
    const map = new Map();
    const isWin = (r) => r?.Win === true || r?.Win === "TRUE" || r?.Win === "Yes";
    const isLoss = (r) => r?.Loss === true || r?.Loss === "TRUE" || r?.Loss === "Yes";

    filteredRows.forEach((r) => {
      const s = (r["Session"] || "").trim() || "Unknown";
      if (!map.has(s)) {
        map.set(s, { session: s, Wins: 0, Losses: 0 });
      }
      if (isWin(r)) map.get(s).Wins += 1;
      else if (isLoss(r)) map.get(s).Losses += 1;
    });

    return Array.from(map.values()).sort(
      (a, b) => b.Wins + b.Losses - (a.Wins + a.Losses)
    );
  }, [filteredRows]);


  const timeframeAgg = useMemo(() => {
    const map = new Map(); 
    (filteredRows || []).forEach((row) => {
      const rawTf = row["Timeframe"] || row["Time Frame"] || row["Time_Frame"];
      const tfCanon = canonicalizeTimeframe(rawTf);
      const minutes = timeframeToMinutes(tfCanon);
      
      if (!tfCanon || !minutes) return;

      if (!map.has(tfCanon)) {
        map.set(tfCanon, {
          label: tfCanon,
          minutes,
          Wins: 0,
          Losses: 0,
          sumWin: 0, nWin: 0,
          sumLoss: 0, nLoss: 0,
        });
      }
      const b = map.get(tfCanon);

      if (isWin(row["Win"])) b.Wins += 1;
      else b.Losses += 1; 

      const reward = parseNum ? parseNum(row["Actual Reward"]) : Number(row["Actual Reward"]);
      if (Number.isFinite(reward)) {
        if (reward > 0) { b.sumWin += reward; b.nWin += 1; }
        else if (reward < 0) { b.sumLoss += reward; b.nLoss += 1; }
      }
    });

    const arr = Array.from(map.values()).map((b) => ({
      ...b,
      AvgWin: b.nWin ? b.sumWin / b.nWin : 0,     
      AvgLoss: b.nLoss ? b.sumLoss / b.nLoss : 0, 
    }));

    arr.sort((a, b) => a.minutes - b.minutes);
    return arr;
  }, [filteredRows]);

  const totalTfTrades = useMemo(
    () => timeframeAgg.reduce((s, x) => s + x.Wins + x.Losses, 0),
    [timeframeAgg]
  );
  const hasTfAverages = useMemo(
    () => timeframeAgg.some(x => x.AvgWin !== 0 || x.AvgLoss !== 0),
    [timeframeAgg]
  );


const quickSuccessionTrades = useMemo(() => {
    const chronologicalRows = [...filteredRows].reverse();

    if (chronologicalRows.length < 2) {
      return 0;
    }

    let count = 0;
    for (let i = 1; i < chronologicalRows.length; i++) {
      const currentTrade = chronologicalRows[i];
      const previousTrade = chronologicalRows[i - 1];

      const currentDate = parseDateVal(currentTrade.Date);
      const currentTimeParts = parseTimeTo24h(currentTrade['Entry Time']);
      const previousDate = parseDateVal(previousTrade.Date);
      const previousTimeParts = parseTimeTo24h(previousTrade['Entry Time']);

      if (!currentDate || !currentTimeParts || !previousDate || !previousTimeParts) {
        continue;
      }

      const currentDateTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentTimeParts[0], currentTimeParts[1]);
      const previousDateTime = new Date(previousDate.getFullYear(), previousDate.getMonth(), previousDate.getDate(), previousTimeParts[0], previousTimeParts[1]);
      
      if (isNaN(currentDateTime.getTime()) || isNaN(previousDateTime.getTime())) {
        continue;
      }

      const diffInMillis = currentDateTime.getTime() - previousDateTime.getTime();
      const diffInMinutes = diffInMillis / 60000;

      if (diffInMinutes >= 0 && diffInMinutes <= 2) {
        count++;
      }
    }

    return count;
  }, [filteredRows]);

const sortedRows = [...filteredRows].sort((a, b) => {
      const dateA = parseDateVal(a["Date"]);
      const dateB = parseDateVal(b["Date"]);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;
      return parseNum(a["Trade #"]) - parseNum(b["Trade #"]);
    });

    // 1. Use the raw, unfiltered journal to safely find the absolute first and last entries
    const allRowsAsc = [...journalRows].sort((a, b) => {
      const timeA = parseDateVal(a["Date"]).getTime() || 0;
      const timeB = parseDateVal(b["Date"]).getTime() || 0;
      if (timeA !== timeB) return timeA - timeB;
      return parseNum(a["Trade #"]) - parseNum(b["Trade #"]);
    });

    // 2. Prioritize pulling the exact balances from Account Settings
    let startingBalance = parseNum(acctSettings?.["Initial Balance"]) || parseNum(acctSettings?.["Beginning Balance"]) || 0;
    let currentBalance = parseNum(acctSettings?.["Running Balance"]) || 0;

    if (allRowsAsc.length > 0) {
      if (startingBalance === 0) {
        const firstEntry = allRowsAsc[0];
        const offset = isFundingRow(firstEntry) ? 0 : parseNum(firstEntry["Actual Reward"]);
        startingBalance = parseNum(firstEntry["Balance Before Trade"]) || (parseNum(firstEntry["Actual Balance"]) - offset);
      }
      if (currentBalance === 0) {
        const lastEntry = allRowsAsc[allRowsAsc.length - 1];
        currentBalance = parseNum(lastEntry["Actual Balance"]);
      }
    }

    // 3. Check if the user has any active filters applied
    const hasActiveFilters = Object.entries(filters).some(([key, val]) => {
      if (key === 'dateFilter') return val !== 'all';
      return val !== '';
    });

    // 4. Calculate overall increase dynamically
    // If unfiltered, show the true total account growth (Running - Starting) to account for deposits/fees
    // If filtered, sum only the visible trades matching the active filters
    const overallIncrease = hasActiveFilters 
      ? filteredRows.reduce((sum, row) => sum + parseNum(row["Actual Reward"]), 0)
      : (currentBalance - startingBalance);

    const overallPct = pctOpenToClose(startingBalance, overallIncrease);

    const todayRows = filteredRows.filter((r) => isToday(parseDateVal(r["Date"])));
    const todayCount = todayRows.length;
    let todayInc = 0, todayPct = 0;
    if (todayCount) {
      todayInc = todayRows.reduce((acc, row) => acc + parseNum(row["Actual Reward"]), 0);
      const firstTradeToday = todayRows.sort((a,b) => parseNum(a['Trade #']) - parseNum(b['Trade #']))[0] || todayRows[0];
      const todayStartBalance = parseNum(firstTradeToday["Actual Balance"]) - parseNum(firstTradeToday["Actual Reward"]);
      todayPct = pctOpenToClose(todayStartBalance, todayInc);
    }

    const weekRows = filteredRows.filter((r) => isThisWeek(parseDateVal(r["Date"]), { weekStartsOn: 1 }));
    const weekCount = weekRows.length;
    let weekInc = 0, weekPct = 0;
    if (weekCount) {
      weekInc = weekRows.reduce((acc, row) => acc + parseNum(row["Actual Reward"]), 0);
      const firstTradeWeek = weekRows.sort((a,b) => parseNum(a['Trade #']) - parseNum(b['Trade #']))[0] || weekRows[0];
      const weekStartBalance = parseNum(firstTradeWeek["Actual Balance"]) - parseNum(firstTradeWeek["Actual Reward"]);
      weekPct = pctOpenToClose(weekStartBalance, weekInc);
    }

  const rainbowWinData = [
    { name: "Wins", value: winCount, fill: "#2563eb" },
    { name: "Losses", value: lossCount, fill: "#FFFF00" },
  ];

  const posMoodRows = filteredRows.filter(
    r => ["1", "2", 1, 2].includes(String(r["Mood/Mindset"]).trim())
  );
  const neuMoodRows = filteredRows.filter(
    r => ["3"].includes(String(r["Mood/Mindset"]).trim())
  );
  const negMoodRows = filteredRows.filter(
    r => ["4", "5", 4, 5].includes(String(r["Mood/Mindset"]).trim())
  );
  
  const buyRows = filteredRows.filter(r => isBuyPos(r["Position"]));
  const sellRows = filteredRows.filter(r => isSellPos(r["Position"]));

  function calcWinPct(rows) {
    if (!rows.length) return 0;
    const winCt = rows.filter(
      r => r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true
    ).length;
    return Math.round((winCt / rows.length) * 100);
  }

  const productList = useMemo(() => {
    const set = new Set();
    journalRows.forEach((r) => {
      const val = shortProduct(r["Product"]);
      if (val) set.add(val);
    });
    return Array.from(set);
  }, [journalRows]);

  const moodIdx = "Mood/Mindset";

  function getMoodVal(row) {
    return (row["Mood/Mindset"] ?? row["Mood_Mindset"] ?? "")
      .toString().trim();
  }

  const moodRows = filteredRows.filter(r => {
    const m = getMoodVal(r);
    return m && ["1", "2", "3", "4", "5"].includes(m);
  });

  const moodTradeCount = moodRows.length;

  const posMood12Count = moodRows.filter(r => ["1", "2"].includes(getMoodVal(r))).length;
  const neutralMood3Count = moodRows.filter(r => ["3"].includes(getMoodVal(r))).length;
  const negMood45Count = moodRows.filter(r => ["4", "5"].includes(getMoodVal(r))).length;

  const winIdx = "Win";
  const winPosMoodCount = filteredRows.filter(r =>
    (["1", "2"].includes(getMoodVal(r))) &&
    (r[winIdx] === "TRUE" || r[winIdx] === "Yes" || r[winIdx] === true)
  ).length;
  const winNeutralMoodCount = filteredRows.filter(r =>
    (["3"].includes(getMoodVal(r))) &&
    (r[winIdx] === "TRUE" || r[winIdx] === "Yes" || r[winIdx] === true)
  ).length;
  const winNegMoodCount = filteredRows.filter(r =>
    (["4", "5"].includes(getMoodVal(r))) &&
    (r[winIdx] === "TRUE" || r[winIdx] === "Yes" || r[winIdx] === true)
  ).length;

  
  // ─────────────────────────── Ticks/Pips helpers & aggregates ───────────────────────────

  const TICK_PIP_PREFERRED_KEYS = [
    "Actual Ticks", "Actual Pips",
    "Ticks", "Pips", "Tcks", "Points", "Cents",
    "P&L (Ticks)", "P&L (Pips)",
    "Ticks/Pips", "Pips/Ticks",
    "Net Ticks", "Total Ticks", "Avg Ticks",
    "Net Pips", "Total Pips", "Avg Pips"
  ];

const tickPipKey = useMemo(() => {
    if (!filteredRows?.length) return null;

    // Auto-detect if we are likely in Forex
    let isForex = false;
    const type = String(acctSettings?.AssetClass || acctSettings?.["Account Type"] || "").toLowerCase();
    if (type.includes("forex")) isForex = true;
    if (!isForex && filteredRows.length > 0 && String(filteredRows[0].assetClass || "").toLowerCase().includes("forex")) isForex = true;

    const counts = {};
    filteredRows.forEach((row) => {
      Object.entries(row || {}).forEach(([k, v]) => {
        const kl = String(k).toLowerCase();
        if (TICK_PIP_PREFERRED_KEYS.some(p => p.toLowerCase() === kl)) {
          if (v === undefined || v === null) return;
          const asStr = typeof v === "string" ? v.trim() : v;
          if (typeof asStr === "string" && asStr === "") return;

          const n = parseNum(v);
          if (Number.isFinite(n)) {
            counts[k] = (counts[k] || 0) + 1;
          }
        }
      });
    });

    const keys = Object.keys(counts);
    if (!keys.length) return null;

    let bestKey = null;
    let bestScore = -Infinity;
    keys.forEach((k) => {
      const kl = String(k).toLowerCase();
      let score = counts[k] * 10;
      
      // Force Pips to the absolute top priority if we are in Forex mode
      if (isForex && (kl === "pips" || kl === "actual pips")) {
          score += 1000;
      } else {
          const prefIdx = TICK_PIP_PREFERRED_KEYS.findIndex(x => x.toLowerCase() === kl);
          score += (prefIdx === -1 ? 0 : 100 - prefIdx);
      }

      if (score > bestScore) { bestScore = score; bestKey = k; }
    });
    return bestKey;
  }, [filteredRows, acctSettings]);

// Indicator to show Win % at the end of stacked bars
  const renderWinPercentageLabel = (winKey, lossKey, data) => (props) => {
    const { x, y, width, height, index } = props;
    const row = data[index];
    if (!row) return null;

    const wins = row[winKey] || 0;
    const losses = row[lossKey] || 0;
    const total = wins + losses;

    if (total === 0) return null;
    
    const winPct = Math.round((wins / total) * 100);

    // Place text at the end of the total stacked bar
    return (
      <text 
        x={x + width + 8} 
        y={y + (height / 2) + 4} 
        fill={isDark ? "#94a3b8" : "#6b7280"} 
        fontSize="12" 
        fontWeight="bold"
      >
        {winPct}%
      </text>
    );
  };

const tickPipUnit = useMemo(() => {
    if (!filteredRows?.length) return "Ticks";
    
    const assetTypes = new Set();
    filteredRows.forEach(r => {
      const ac = String(r.assetClass || r["Account Type"] || acctSettings?.["Account Type"] || "").toLowerCase();
      if (ac.includes("forex")) assetTypes.add("forex");
      else if (ac.includes("future")) assetTypes.add("futures");
      else if (ac.includes("stock") || ac.includes("option")) assetTypes.add("points");
    });

    // MIXED ACCOUNTS -> Display "Ticks"
    if (assetTypes.size > 1) return "Ticks";
    
    // SINGLE ASSET ACCOUNTS -> Use native unit
    if (assetTypes.has("forex")) return "Pips";
    if (assetTypes.has("points")) return "Points";
    return "Ticks"; 
  }, [filteredRows, acctSettings]);


const extractRawTickPip = (row) => {
    if (!tickPipKey) return null;
    const raw = row[tickPipKey];
    if (raw === undefined || raw === null || raw === "") return null;
    let n = parseNum(raw);
    if (!Number.isFinite(n)) return null;

    // Redundant tickSize conversion removed; the DB value is already in Ticks/Pips
    return n;
  };

  // ───────────────────────── Potential (Ticks/Pips) helpers ─────────────────────────

  const potentialKey = useMemo(() => {
    if (!filteredRows?.length) return null;

    const counts = {};
    filteredRows.forEach((row) => {
      Object.entries(row || {}).forEach(([k, v]) => {
        if (!String(k).toLowerCase().includes("potential")) return;
        if (v === undefined || v === null) return;
        if (typeof v === "string" && v.trim() === "") return;

        const n = parseNum(v);
        if (Number.isFinite(n)) counts[k] = (counts[k] || 0) + 1;
      });
    });

    const keys = Object.keys(counts);
    if (!keys.length) return null;

    const pref = [
      "Potential Ticks",
      "Potential Pips",
      "Potential Points",
      "Potential (Ticks)",
      "Potential (Pips)",
      "Potential",
    ].map((s) => s.toLowerCase());

    let bestKey = null;
    let bestScore = -Infinity;

    keys.forEach((k) => {
      const idx = pref.indexOf(String(k).toLowerCase());
      const bonus = idx === -1 ? 0 : (100 - idx); 
      const score = counts[k] * 10 + bonus;
      if (score > bestScore) {
        bestScore = score;
        bestKey = k;
      }
    });

    return bestKey;
  }, [filteredRows]);

const extractPotential = (row) => {
      const raw = row["Potential"] ?? row["Potential Pips"] ?? row["Potential Ticks"] ?? row["Potential Points"];
      if (raw === undefined || raw === null || raw === "") return 0;
      
      let n = Number(String(raw).replace(/[^\d.-]/g, ""));
      if (!Number.isFinite(n)) return 0;
      
      // ONLY CONVERT IF IT'S DISPLAYING AS TICKS
      if (tickPipUnit === "Ticks") {
          const ac = String(row.assetClass || row["Account Type"] || acctSettings?.["Account Type"] || "").toLowerCase();
          if (ac.includes("forex")) n = n * 10; // Pips to Ticks
          else if (ac.includes("stock") || ac.includes("option")) n = n * 100; // Points to Ticks
      }
      
      return n;
  };

const avgPotentialWin = useMemo(() => {
  if (!potentialKey) return null;
  const vals = (filteredRows || [])
    .filter((r) => isWin(r["Win"]))
    .map(extractPotential)
    .filter((n) => n != null && n !== 0);

  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}, [filteredRows, potentialKey]);

const avgPotentialLoss = useMemo(() => {
  if (!potentialKey) return null;
  const vals = (filteredRows || [])
    .filter((r) => isLossFlag(r["Loss"]))
    .map(extractPotential)
    .filter((n) => n != null && n !== 0);

  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}, [filteredRows, potentialKey]);


  function isLossFlag(v) {
    if (v === true) return true;
    const s = String(v ?? "").toLowerCase();
    return s === "true" || s === "yes";
  }

const getSignedTickPip = (row) => {
    // 1. Determine Win/Loss Sign
    let sign = 1;
    const isLossFlag = String(row["Loss"]).toLowerCase() === "true" || row["Loss"] === true;
    const pnl = Number(row["Actual Reward"] || 0);
    if (isLossFlag || pnl < 0) sign = -1;

    // 2. Read EXACTLY what was saved in the database
    // Use the dynamically determined tickPipKey first (prioritizes Pips for Forex)
    let val = tickPipKey ? row[tickPipKey] : null;

    // Fallback if the preferred key is empty for this specific row
    if (val == null || val === "") {
        val = row["Movement"] ?? row["Actual Pips"] ?? row["Pips"] ?? row["Actual Ticks"] ?? row["Ticks"] ?? row["Points"] ?? row["Tcks"];
    }
    
    // 3. Fallback only if database field is totally blank
    if (val == null || val === "") {
        const entry = Number(row["Entry Price"]);
        const exit = Number(row["Exit Price"]);
        if (entry > 0 && exit > 0) val = Math.abs(exit - entry); 
    }

    let finalNum = Math.abs(Number(val));
    if (isNaN(finalNum) || finalNum === 0) return 0;

    // 4. THE ONLY CONVERSION: Convert to Ticks if the global view is Ticks
    if (tickPipUnit === "Ticks") {
        const ac = String(row.assetClass || row["Account Type"] || acctSettings?.["Account Type"] || "").toLowerCase();
        if (ac.includes("forex")) {
            finalNum = finalNum * 10; // Forex Pips -> Ticks
        } else if (ac.includes("stock") || ac.includes("option")) {
            finalNum = finalNum * 100; // Stocks/Options Points -> Ticks
        }
    }

    // Return the pure number with the correct +/- sign (NO POSITION SIZE MULTIPLIER)
    return finalNum * sign;
};

  
  const overallVals = useMemo(() => {
    return (filteredRows || [])
      .map(getSignedTickPip)
      .filter(v => v != null && v !== 0);
  }, [filteredRows, tickPipKey]);

  const overallAvgTickPip = overallVals.length
    ? overallVals.reduce((a, b) => a + b, 0) / overallVals.length
    : 0;

  const overallTotalTickPip = useMemo(() => {
    return (overallVals || []).reduce((a, b) => a + b, 0);
  }, [overallVals]);

  const todayVals = useMemo(() => {
    return (todayRows || [])
      .map(getSignedTickPip)
      .filter(v => v != null && v !== 0);
  }, [todayRows, tickPipKey]);

  const todayAvgTickPip = todayVals.length
    ? todayVals.reduce((a, b) => a + b, 0) / todayVals.length
    : 0;

  const todayTotalTickPip = todayVals.reduce((a, b) => a + b, 0);

  const weekVals = useMemo(() => {
    return (weekRows || [])
      .map(getSignedTickPip)
      .filter(v => v != null && v !== 0);
  }, [weekRows, tickPipKey]);

  const weekAvgTickPip = weekVals.length
    ? weekVals.reduce((a, b) => a + b, 0) / weekVals.length
    : 0;

  const weekTotalTickPip = weekVals.reduce((a, b) => a + b, 0);

  const hasTickPipData = useMemo(
    () => !!tickPipKey && filteredRows.some(r => extractRawTickPip(r) != null),
    [tickPipKey, filteredRows]
  );

  const avgTicksWin = useMemo(() => {
    const vals = filteredRows
      .filter(r => isWin(r["Win"]))
      .map(getSignedTickPip)
      .filter(n => n != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [filteredRows, tickPipKey]);

  const avgTicksLoss = useMemo(() => {
    const vals = filteredRows
      .filter(r => isLossFlag(r["Loss"]))
      .map(getSignedTickPip)
      .filter(n => n != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [filteredRows, tickPipKey]);

  const productTickStats = useMemo(() => {
    const map = new Map(); 
    filteredRows.forEach((row) => {
      const val = getSignedTickPip(row);
      if (val == null) return;
      const prod = shortProduct(row["Product"]);
      if (!prod) return;
      const bucket = map.get(prod) || { sum: 0, n: 0 };
      bucket.sum += val;
      bucket.n += 1;
      map.set(prod, bucket);
    });
    return Array.from(map.entries()).map(([product, { sum, n }]) => ({
      product, avg: n ? sum / n : 0, n
    }));
  }, [filteredRows, tickPipKey]);

  const bestAvgTickProd = useMemo(
    () => (productTickStats.length ? productTickStats.reduce((m, x) => (x.avg > m.avg ? x : m), productTickStats[0]) : null),
    [productTickStats]
  );
  const worstAvgTickProd = useMemo(
    () => (productTickStats.length ? productTickStats.reduce((m, x) => (x.avg < m.avg ? x : m), productTickStats[0]) : null),
    [productTickStats]
  );

  const avgTicksByTimePeriod = useMemo(() => {
    const buckets = [
      { label: "12:00 am - 2:00 am",  start: 0,  end: 2,  sum: 0, n: 0 },
      { label: "2:00 am - 4:00 am",   start: 2,  end: 4,  sum: 0, n: 0 },
      { label: "4:00 am - 6:00 am",   start: 4,  end: 6,  sum: 0, n: 0 },
      { label: "6:00 am - 8:00 am",   start: 6,  end: 8,  sum: 0, n: 0 },
      { label: "8:00 am - 10:00 am",  start: 8,  end: 10, sum: 0, n: 0 },
      { label: "10:00 am - 12:00 pm", start: 10, end: 12, sum: 0, n: 0 },
      { label: "12:00 pm - 2:00 pm",  start: 12, end: 14, sum: 0, n: 0 },
      { label: "2:00 pm - 4:00 pm",   start: 14, end: 16, sum: 0, n: 0 },
      { label: "4:00 pm - 6:00 pm",   start: 16, end: 18, sum: 0, n: 0 },
      { label: "6:00 pm - 8:00 pm",   start: 18, end: 20, sum: 0, n: 0 },
      { label: "8:00 pm - 10:00 pm",  start: 20, end: 22, sum: 0, n: 0 },
      { label: "10:00 pm - 12:00 am", start: 22, end: 24, sum: 0, n: 0 },
    ];

    filteredRows.forEach((row) => {
      const tp = getSignedTickPip(row);
      if (tp == null) return;
      const entry = row["Entry Time"];
      if (!entry || typeof entry !== "string") return;

      const parts = parseTimeTo24h(entry);
      if (!parts) return;
      const [h] = parts;

      const b = buckets.find(b => h >= b.start && h < b.end);
      if (!b) return;
      b.sum += tp;
      b.n += 1;
    });

    return buckets.map(b => ({
      label: b.label,
      Avg: b.n ? b.sum / b.n : 0,
      Count: b.n
    }));
  }, [filteredRows, tickPipKey]);

  const tpDomain = useMemo(() => {
    const vals = (avgTicksByTimePeriod || []).map(b => Number(b.Avg) || 0);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    return min === max ? [-1, 1] : [min, max];   
  }, [avgTicksByTimePeriod]);



  const TicksAvgTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const avg = payload[0]?.value ?? 0;
    const n = payload[0]?.payload?.Count ?? 0;
    return (
      <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
        <p className="font-bold text-sm mb-2">{label}</p>
        <p className="text-sm">Avg: {avg.toFixed(1)} {tickPipUnit}</p>
        <p className="text-xs text-gray-300">Trades: {n}</p>
      </div>
    );
  };

  const posIdx = "Position";
  
  const buyCount = filteredRows.filter(r => isBuyPos(r[posIdx])).length;
  const buyWinCount = filteredRows.filter(r =>
    isBuyPos(r[posIdx]) &&
    (r[winIdx] === "TRUE" || r[winIdx] === "Yes" || r[winIdx] === true)
  ).length;
  
  const sellCount = filteredRows.filter(r => isSellPos(r[posIdx])).length;
  const sellWinCount = filteredRows.filter(r =>
    isSellPos(r[posIdx]) &&
    (r[winIdx] === "TRUE" || r[winIdx] === "Yes" || r[winIdx] === true)
  ).length;

  const winRatePositive = posMood12Count ? Math.round((winPosMoodCount / posMood12Count) * 100) : 0;
  const winRateNeutral = neutralMood3Count ? Math.round((winNeutralMoodCount / neutralMood3Count) * 100) : 0;
  const winRateNegative = negMood45Count ? Math.round((winNegMoodCount / negMood45Count) * 100) : 0;
  const lossRatePositive = posMood12Count > 0 ? 100 - winRatePositive : 0;
  const lossRateNeutral = neutralMood3Count > 0 ? 100 - winRateNeutral : 0;
  const lossRateNegative = negMood45Count > 0 ? 100 - winRateNegative : 0;


const dayAgg = useMemo(() => {
  const byDay = {};
  (filteredRows || []).forEach((r) => {
    if (!r?.Date) return;
 const key = toKeyYMD(r["Date"]);
 if (!key) return;
    const pnl = parseNum(r["Actual Reward"]);
    if (!byDay[key]) byDay[key] = { pnl: 0, trades: 0 };
    byDay[key].pnl += pnl;
    byDay[key].trades += 1;
  });

  const days = Object.values(byDay);
  const winDays  = days.filter(d => d.pnl > 0);
  const lossDays = days.filter(d => d.pnl < 0);

  const sum = (arr, sel) => arr.reduce((s, x) => s + sel(x), 0);

  const avgWinDayPnL          = winDays.length  ? sum(winDays,  d => d.pnl)    / winDays.length  : 0;
  const avgTradesOnWinDays    = winDays.length  ? sum(winDays,  d => d.trades) / winDays.length  : 0;

  const avgLossDayPnL         = lossDays.length ? sum(lossDays, d => d.pnl)    / lossDays.length : 0;
  const avgTradesOnLossDays   = lossDays.length ? sum(lossDays, d => d.trades) / lossDays.length : 0;

  return {
    avgWinDayPnL,
    avgTradesOnWinDays,
    avgLossDayPnL,
    avgTradesOnLossDays,
    winDayCount:  winDays.length,
    lossDayCount: lossDays.length,
  };
}, [filteredRows]);


const isOptionsOnlyContext = useMemo(() => {
    const globalType = String(acctSettings?.AssetClass || acctSettings?.["Account Type"] || acctSettings?.type || "").toLowerCase();
    
    // 1. If explicitly set to options globally
    if (globalType === "options") return true;

    if (filteredRows.length > 0) {
      const allAreOptions = filteredRows.every(r => {
        const rType = String(r["Account Type"] || r["assetClass"] || "").toLowerCase();
        
        // 2. If the row explicitly says it's forex, futures, or stock, it is NOT an option
        if (rType.includes("forex") || rType.includes("future") || rType.includes("stock")) {
            return false;
        }
        
        // 3. Only count as options if it has a real, typed-in Strike price
        const hasOptData = r["Strike"] !== undefined && String(r["Strike"]).trim() !== "";
        return rType === "options" || hasOptData;
      });
      
      if (allAreOptions) return true;
    }

    return false;
  }, [acctSettings, filteredRows]);


  const stopLossStats = useMemo(() => {
    let winSLSum = 0, winSLCount = 0;
    let lossSLSum = 0, lossSLCount = 0;
    let winIdealSum = 0, winIdealCount = 0;
    let lossIdealSum = 0, lossIdealCount = 0;

    const timeBuckets = [
      { label: "12am-2am", start: 0, end: 2 },
      { label: "2am-4am", start: 2, end: 4 },
      { label: "4am-6am", start: 4, end: 6 },
      { label: "6am-8am", start: 6, end: 8 },
      { label: "8am-10am", start: 8, end: 10 },
      { label: "10am-12pm", start: 10, end: 12 },
      { label: "12pm-2pm", start: 12, end: 14 },
      { label: "2pm-4pm", start: 14, end: 16 },
      { label: "4pm-6pm", start: 16, end: 18 },
      { label: "6pm-8pm", start: 18, end: 20 },
      { label: "8pm-10pm", start: 20, end: 22 },
      { label: "10pm-12am", start: 22, end: 24 },
    ].map(b => ({
      ...b,
      wSLSum: 0, wSLCount: 0, wIdealSum: 0, wIdealCount: 0,
      lSLSum: 0, lSLCount: 0, lIdealSum: 0, lIdealCount: 0
    }));

    const getStopLossData = (row) => {
          let stopLoss = null;
          let idealSL = null;

          const slTpStr = String(row["Stop Loss_Take Profit"] || "").trim();
          if (slTpStr) {
            const parts = slTpStr.split("/");
            const slPart = parts[0]; 
            
            const match = slPart.match(/(-?\d+(\.\d+)?)/);
            if (match) {
               const val = parseFloat(match[0]);
               if (Number.isFinite(val)) stopLoss = val;
            }
          }
          
          if (stopLoss === null && row["Stop Loss"] !== undefined) {
            const val = parseFloat(row["Stop Loss"]);
            if (Number.isFinite(val)) stopLoss = val;
          }


      const normalizeKey = (s) =>
        String(s)
          .replace(/\u00A0/g, " ")     
          .replace(/\s+/g, " ")        
          .trim()
          .toLowerCase();

      const idealKey = Object.keys(row).find(k => {
        const nk = normalizeKey(k);
        return nk === "idealsl" || nk === "ideal stop loss" || nk === "ideal sl";
      });

      const idealStr = idealKey != null ? String(row[idealKey] ?? "").trim() : "";
      if (idealStr) {
        const match = idealStr.match(/(-?\d+(\.\d+)?)/); 
        if (match) {
          const val = parseFloat(match[0]);
          if (Number.isFinite(val)) idealSL = val;
        }
      }
      return { stopLoss, idealSL };
    };

    filteredRows.forEach((row) => {
      const { stopLoss, idealSL } = getStopLossData(row);
      const isWinRow = isWin(row["Win"]);
      const isLossRow = isLossFlag(row["Loss"]);

      if (stopLoss !== null) {
        if (isWinRow) { winSLSum += stopLoss; winSLCount++; }
        else if (isLossRow) { lossSLSum += stopLoss; lossSLCount++; }
      }
      if (idealSL !== null) {
        if (isWinRow) { winIdealSum += idealSL; winIdealCount++; }
        else if (isLossRow) { lossIdealSum += idealSL; lossIdealCount++; }
      }

      const entryTime = row["Entry Time"];
      if (entryTime) {
        const parts = parseTimeTo24h(entryTime);
        if (parts) {
          const [h] = parts;
          const bucket = timeBuckets.find(b => h >= b.start && h < b.end);
          if (bucket) {
            if (isWinRow) {
              if (stopLoss !== null) { bucket.wSLSum += stopLoss; bucket.wSLCount++; }
              if (idealSL !== null) { bucket.wIdealSum += idealSL; bucket.wIdealCount++; }
            } else if (isLossRow) {
              if (stopLoss !== null) { bucket.lSLSum += stopLoss; bucket.lSLCount++; }
              if (idealSL !== null) { bucket.lIdealSum += idealSL; bucket.lIdealCount++; }
            }
          }
        }
      }
    });

    const avgWinSL = winSLCount ? winSLSum / winSLCount : 0;
    const avgLossSL = lossSLCount ? lossSLSum / lossSLCount : 0;
    const avgWinIdeal = winIdealCount ? winIdealSum / winIdealCount : 0;
    const avgLossIdeal = lossIdealCount ? lossIdealSum / lossIdealCount : 0;

    const chartData = timeBuckets.map(b => ({
      name: b.label,
      "Win Actual": b.wSLCount ? Number((b.wSLSum / b.wSLCount).toFixed(2)) : 0,
      "Win Ideal": b.wIdealCount ? Number((b.wIdealSum / b.wIdealCount).toFixed(2)) : 0,
      "Loss Actual": b.lSLCount ? Number((b.lSLSum / b.lSLCount).toFixed(2)) : 0,
      "Loss Ideal": b.lIdealCount ? Number((b.lIdealSum / b.lIdealCount).toFixed(2)) : 0,
    }));

    return { avgWinSL, avgLossSL, avgWinIdeal, avgLossIdeal, chartData };
  }, [filteredRows]);

const hasIdealSLData = useMemo(() => {
  const hasBars = (stopLossStats?.chartData || []).some(
    d => (Number(d["Win Ideal"]) || 0) > 0 || (Number(d["Loss Ideal"]) || 0) > 0
  );
  const hasAvgs =
    (Number(stopLossStats?.avgWinIdeal) || 0) > 0 ||
    (Number(stopLossStats?.avgLossIdeal) || 0) > 0;

  return hasBars || hasAvgs;
}, [stopLossStats]);


  const SLTimeTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
        <p className="font-bold text-sm mb-2">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm" style={{ color: p.color }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  };

const potentialVsActualByTime = useMemo(() => {
    const buckets = [
      { label: "12am-2am", start: 0, end: 2 },
      { label: "2am-4am", start: 2, end: 4 },
      { label: "4am-6am", start: 4, end: 6 },
      { label: "6am-8am", start: 6, end: 8 },
      { label: "8am-10am", start: 8, end: 10 },
      { label: "10am-12pm", start: 10, end: 12 },
      { label: "12pm-2pm", start: 12, end: 14 },
      { label: "2pm-4pm", start: 14, end: 16 },
      { label: "4pm-6pm", start: 16, end: 18 },
      { label: "6pm-8pm", start: 18, end: 20 },
      { label: "8pm-10pm", start: 20, end: 22 },
      { label: "10pm-12am", start: 22, end: 24 },
    ].map(b => ({
      ...b,
      wActSum: 0, wActCount: 0, wPotSum: 0, wPotCount: 0,
      lActSum: 0, lActCount: 0, lPotSum: 0, lPotCount: 0
    }));

    filteredRows.forEach(row => {
        const entryTime = row["Entry Time"];
        if (!entryTime) return;

        const parts = parseTimeTo24h(entryTime);
        if (!parts) return;
        const [h] = parts;
        
        const bucket = buckets.find(b => h >= b.start && h < b.end);
        if (!bucket) return;

        const isWinRow = isWin(row["Win"]);
        const isLossRow = isLossFlag(row["Loss"]);
        
        const actual = getSignedTickPip(row); 
        const potential = extractPotential(row); 

        if (isWinRow) {
            if (actual != null && actual !== 0) { bucket.wActSum += actual; bucket.wActCount++; }
            if (potential != null && potential !== 0) { bucket.wPotSum += potential; bucket.wPotCount++; }
        } else if (isLossRow) {
            if (actual != null && actual !== 0) { bucket.lActSum += actual; bucket.lActCount++; }
            if (potential != null && potential !== 0) { bucket.lPotSum += potential; bucket.lPotCount++; }
        }
    });

    return buckets.map(b => ({
        name: b.label,
        "Win Actual": b.wActCount ? Number((b.wActSum / b.wActCount).toFixed(1)) : 0,
        "Win Potential": b.wPotCount ? Number((b.wPotSum / b.wPotCount).toFixed(1)) : 0,
        "Loss Actual": b.lActCount ? Number((b.lActSum / b.lActCount).toFixed(1)) : 0,
        "Loss Potential": b.lPotCount ? Number((b.lPotSum / b.lPotCount).toFixed(1)) : 0,
    }));
  }, [filteredRows, tickPipKey]); 

  const PotVsActTimeTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
        <p className="font-bold text-sm mb-2">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm" style={{ color: p.color }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  };


 // ───────────────────────────── Pre-Trade Checklist (by day) ─────────────────────────────

  
   useEffect(() => {
     if (!userId) return;
     getUserSettings(userId).then(setUserPrefs).catch(() => setUserPrefs({}));
   }, [userId]);


const yesNo = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "yes") return true;
  if (s === "no")  return false;
  return false; 
};

const [pretradeEnabled, setPretradeEnabled] = useState(false);
const [prefsLoaded, setPrefsLoaded] = useState(false);

const ptcDayKeys = useMemo(() => {
  const s = new Set();
  (filteredRows || []).forEach((r) => {
    if (!r?.Date) return;
    try {
      const key = format(parseDateVal(r["Date"]), "yyyy-MM-dd");
      if (key) s.add(key);
    } catch {}
  });
  return Array.from(s);
}, [filteredRows]);

useEffect(() => {
  if (!userId) return;
  const db = getFirestore();
  const ref = doc(db, "users", userId, "settings", "prefs");

  const unsub = onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setPretradeEnabled(yesNo(data["Pretrade Checklist"]));
      setPrefsLoaded(true);
    },
    () => {
      setPretradeEnabled(false);
      setPrefsLoaded(true);
    }
  );
  return () => unsub();
}, [userId]);

useEffect(() => {
  let cancel = false;
  async function run() {
    if (!prefsLoaded || !pretradeEnabled || !userId || !accountId || ptcDayKeys.length === 0) {
      setPtcByDay({});
      return;
    }
  }
  run();
}, [prefsLoaded, pretradeEnabled, userId, accountId, ptcDayKeys]);

const [ptcByDay, setPtcByDay] = useState({});

useEffect(() => {
    let cancel = false;
    async function run() {
      if (!pretradeEnabled || !userId || !accountId || ptcDayKeys.length === 0) {
        setPtcByDay({});
        return;
      }
    const entries = await Promise.all(
      ptcDayKeys.map(async (dayKey) => {
        try {
          const data = await getTodayPreTradeChecklist(userId, accountId, dayKey);
          if (!data || typeof data !== "object") return [dayKey, 0];

                    let total = 0, checked = 0;
                    const skipKeys = new Set([
                      'id', 'date', 'userid', 'accountid', 'createdat', 'updatedat', 
                      'timestamp', 'version', '_path', 'notes', 'comment', 'description'
                    ]);

                    for (const [key, v] of Object.entries(data)) {
                      if (skipKeys.has(key.toLowerCase())) continue;

                      if (typeof v === "boolean") { 
                        total++; 
                        if (v) checked++; 
                        continue; 
                      }
                      
                      if (typeof v === "number") { 
                         if (v === 1 || v === 0) {
                           total++; 
                           if (v === 1) checked++; 
                         }
                         continue; 
                      }

                      if (typeof v === "string") {
                        const s = v.trim().toLowerCase();
                        const yes = ["yes","true","1","on","y","✓"].includes(s);
                        const no  = ["no","false","0","off","n","✗"].includes(s);
                        if (yes || no) { 
                          total++; 
                          if (yes) checked++; 
                        }
                      }
                    }
          const ratio = total > 0 ? (checked / total) : 0;
          return [dayKey, ratio];
        } catch {
          return [dayKey, 0];
        }
      })
    );
    if (!cancel) {
      const m = {};
      entries.forEach(([k, v]) => { m[k] = v; });
      setPtcByDay(m);
    }
  }
  run();
  return () => { cancel = true; };
}, [userId, accountId, ptcDayKeys, pretradeEnabled]);

const HALF_TOL = 0.10; 
const ptcBuckets = useMemo(() => {
  const b = { P100: [], P50: [], P0: [] };
  ptcDayKeys.forEach((d) => {
    const r = (ptcByDay[d] ?? 0);
    if (r >= 0.99) b.P100.push(d);
    else if (Math.abs(r - 0.5) <= HALF_TOL) b.P50.push(d);
    else if (r <= 0.01) b.P0.push(d);
  });
  return b;
}, [ptcDayKeys, ptcByDay]);

const tradesByDay = useMemo(() => {
  const map = {};
  (filteredRows || []).forEach((r) => {
    if (!r?.Date) return;
    try {
      const key = format(parseDateVal(r["Date"]), "yyyy-MM-dd");
      if (!key) return;
      (map[key] ||= []).push(r);
    } catch {}
  });
  return map;
}, [filteredRows]);

const dayCountAll = ptcDayKeys.length;
const days100 = ptcBuckets.P100.length;
const days50  = ptcBuckets.P50.length;
const days0   = ptcBuckets.P0.length;

const pctDays100 = dayCountAll ? Math.round((days100 / dayCountAll) * 100) : 0;
const pctDays50  = dayCountAll ? Math.round((days50  / dayCountAll) * 100) : 0;
const pctDays0   = dayCountAll ? Math.round((days0   / dayCountAll) * 100) : 0;

const isWinRow = (r) => r?.Win === true || r?.Win === "TRUE" || r?.Win === "Yes";
function perfFor(dayList) {
  const trades = dayList.flatMap((d) => tradesByDay[d] || []);
 const wins   = trades.filter(isWinRow).length;
 const losses = trades.length - wins;
 const total  = trades.length;
 return {
   winPct: total ? Math.round((wins / total) * 100) : 0,
   lossPct: total ? Math.round((losses / total) * 100) : 0,
 wins,
 losses,
   total
 };
}
const perf100 = perfFor(ptcBuckets.P100);
const perf50  = perfFor(ptcBuckets.P50);
const perf0   = perfFor(ptcBuckets.P0);


  const gradeStats = useMemo(() => {
    const gradeMap = { 'A': 4, 'A+': 4, 'A-': 3.7, 'B+': 3.3, 'B': 3, 'B-': 2.7, 'C+': 2.3, 'C': 2, 'C-': 1.7, 'D+': 1.3, 'D': 1, 'F': 0 };
    const gradedTrades = filteredRows.filter(r => r.Grade && gradeMap[r.Grade.toUpperCase()] !== undefined);

    if (gradedTrades.length === 0) {
      return { average: null, count: 0 };
    }

    const totalPoints = gradedTrades.reduce((acc, row) => acc + gradeMap[row.Grade.toUpperCase()], 0);
    const averagePoints = totalPoints / gradedTrades.length;
    
    let averageGrade = 'N/A';
    if (averagePoints >= 3.85) averageGrade = 'A';
    else if (averagePoints >= 3.5) averageGrade = 'A-';
    else if (averagePoints >= 3.15) averageGrade = 'B+';
    else if (averagePoints >= 2.85) averageGrade = 'B';
    else if (averagePoints >= 2.5) averageGrade = 'B-';
    else if (averagePoints >= 2.15) averageGrade = 'C+';
    else if (averagePoints >= 1.85) averageGrade = 'C';
    else if (averagePoints >= 1.5) averageGrade = 'C-';
    else if (averagePoints >= 1.15) averageGrade = 'D+';
    else if (averagePoints >= 0.5) averageGrade = 'D';
    else averageGrade = 'F';

    return { average: averageGrade, count: gradedTrades.length };
  }, [filteredRows]);


  let bestProduct = "";
  let maxWins = 0;
  const productWinMap = {};
  filteredRows.forEach((r) => {
    const prod = (r["Product"] || "").split(" - ")[0];
    if (!prod) return;
    if (!productWinMap[prod]) productWinMap[prod] = { wins: 0, trades: 0 };
    productWinMap[prod].trades++;
    if (r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true) {
      productWinMap[prod].wins++;
    }
  });
  Object.entries(productWinMap).forEach(([k, v]) => {
    if (v.wins > maxWins) {
      maxWins = v.wins;
      bestProduct = k;
    }
  });

  let worstProduct = "";
  let maxLosses = 0;
  const productLossMap = {};
  filteredRows.forEach((r) => {
    const prod = (r["Product"] || "").split(" - ")[0];
    if (!prod) return;
    if (!productLossMap[prod]) productLossMap[prod] = 0;
    if (r["Loss"] === "TRUE" || r["Loss"] === "Yes" || r["Loss"] === true) {
      productLossMap[prod]++;
      if (productLossMap[prod] > maxLosses) {
        maxLosses = productLossMap[prod];
        worstProduct = prod;
      }
    }
  });

  const bestTrade = filteredRows.reduce(
    (best, row) => {
      const rew = parseNum(row["Actual Reward"]);
      return rew > best.rew ? { rew, row } : best;
    },
    { rew: -Infinity, row: null }
  );
  const worstTrade = filteredRows.reduce(
    (worst, row) => {
      const rew = parseNum(row["Actual Reward"]);
      return rew < worst.rew ? { rew, row } : worst;
    },
    { rew: Infinity, row: null }
  );

  const durationIdx = "Duration (h:mm)"; 
  const durationRows = filteredRows
    .map((r) => ({ row: r, dur: getAndRecalculateDuration(r) }))
    .filter((x) => x.dur);
    
  let longestTrade = { dur: "", idx: -1 };
  let shortestTrade = { dur: "", idx: -1 };
  let maxSeconds = 0;
  let minSeconds = Infinity;

  durationRows.forEach((item, i) => {
    const parts = (item.dur || "0:0:0").split(':').map(Number);
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    const totalSeconds = (h * 3600) + (m * 60) + s;

    if (totalSeconds > maxSeconds) {
      maxSeconds = totalSeconds;
      longestTrade = { dur: item.dur, idx: i };
    }
    if (totalSeconds > 0 && totalSeconds < minSeconds) {
      minSeconds = totalSeconds;
      shortestTrade = { dur: item.dur, idx: i };
    }
  });

  function parseRiskToReward(value) {
    if (value == null) return null;
    const str = String(value).trim();

        const colon = str.match(/^\s*(-?\d*\.?\d+)\s*:\s*(-?\d*\.?\d+)\s*$/);
        if (colon) {
          const a = parseFloat(colon[1]);   
          const b = parseFloat(colon[2]);   
          const isOne = (x) => isFinite(x) && Math.abs(Math.abs(x) - 1) < 1e-9;
          if (isFinite(a) && isFinite(b) && a !== 0 && b !== 0) {
            if (isOne(a) && !isOne(b)) return b / Math.abs(a); 
            if (isOne(b) && !isOne(a)) return a / Math.abs(b); 
            return b / Math.abs(a);
          }
          return null;
        }

    const single = str.match(/-?\d*\.?\d+/);
    if (single) {
      const val = parseFloat(single[0]);
      return isFinite(val) ? val : null;
    }
    return null;
  }


  const formatterCurrency = useMemo(
    () => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }),
    []
  );


const makeHLabel = (fmt) => (props) => {
  const { x = 0, y = 0, width = 0, height = 0, value, payload } = props;
  if (payload == null) return null;

  const actualVal =
    typeof payload.Avg === "number"
      ? payload.Avg
      : typeof value === "number"
      ? value
      : 0;

  const isNeg = actualVal < 0;
  const insidePad = 6;
  const outsidePad = 8;
  const canFitInside = !isNeg && Math.abs(width) >= 24;

  const xInside = x + width - insidePad;
  const xOutside = isNeg ? x - outsidePad : x + width + outsidePad;
  const anchorInside = "end";
  const anchorOutside = isNeg ? "end" : "start";

  const insideFill = "#ffffff";
  const outsideFill = isNeg
    ? (isDark ? "#1a1a1a" : "#111111") 
    : (isDark ? "#ffffff" : "#111827");

  const formatted =
    typeof fmt === "function" ? fmt(actualVal) : actualVal.toFixed(1);

  const common = {
    y: y + height / 2,
    dy: 4,
    fontSize: 12,
    fontWeight: 700,
    pointerEvents: "none",
    style: { filter: "drop-shadow(0 0 1px rgba(0,0,0,0.15))" },
  };

  return (
    <text
      x={canFitInside ? xInside : xOutside}
      textAnchor={canFitInside ? anchorInside : anchorOutside}
      fill={canFitInside ? insideFill : outsideFill}
      {...common}
    >
      {formatted}
    </text>
  );
};






  const labelCount   = makeHLabel((n) => `${Math.round(n)}`);
  const labelOneDec  = makeHLabel((n) => n.toFixed(1));
  const labelPercent = makeHLabel((n) => `${Math.round(n)}%`);
  const labelMoney   = makeHLabel((n) => formatterCurrency.format(n));

  const winsLossByEntry = useMemo(() => {
    const map = new Map();

    const isWin = (r) =>
      r?.Win === true || r?.Win === "TRUE" || r?.Win === "Yes";
    const isLoss = (r) =>
      r?.Loss === true || r?.Loss === "TRUE" || r?.Loss === "Yes";

    const normalizeStrategy = (val) => {
      if (val == null) return "Unknown";
      const s = String(val).trim();
      if (
        !s ||
        s === "-" ||
        s.toLowerCase() === "n/a" ||
        s.toLowerCase() === "none" ||
        s.toLowerCase() === "unknown"
      ) {
        return "Unknown";
      }
      return s;
    };

    filteredRows.forEach((r) => {
      const key = normalizeStrategy(r["Entry Strategy"]);

      if (!map.has(key)) {
        map.set(key, { strategy: key, Wins: 0, Losses: 0 });
      }

      if (isWin(r)) map.get(key).Wins += 1;
      else if (isLoss(r)) map.get(key).Losses += 1;
    });

    return Array.from(map.values()).sort(
      (a, b) => b.Wins + b.Losses - (a.Wins + a.Losses)
    );
  }, [filteredRows]);

  const winsLossByExit = useMemo(() => {
    const map = new Map();

    const isWin = (r) =>
      r?.Win === true || r?.Win === "TRUE" || r?.Win === "Yes";
    const isLoss = (r) =>
      r?.Loss === true || r?.Loss === "TRUE" || r?.Loss === "Yes";

    const normalizeStrategy = (val) => {
      if (val == null) return "Unknown";
      const s = String(val).trim();
      if (
        !s ||
        s === "-" ||
        s.toLowerCase() === "n/a" ||
        s.toLowerCase() === "none" ||
        s.toLowerCase() === "unknown"
      ) {
        return "Unknown";
      }
      return s;
    };

    filteredRows.forEach((r) => {
      const key = normalizeStrategy(r["Exit Strategy"]);

      if (!map.has(key)) {
        map.set(key, { strategy: key, Wins: 0, Losses: 0 });
      }

      if (isWin(r)) map.get(key).Wins += 1;
      else if (isLoss(r)) map.get(key).Losses += 1;
    });

    return Array.from(map.values()).sort(
      (a, b) => b.Wins + b.Losses - (a.Wins + a.Losses)
    );
  }, [filteredRows]);

   function formatRiskToReward(ratio) {
     if (ratio == null || !isFinite(ratio)) return "--";
     const sign = ratio < 0 ? "-" : "";
     const abs = Math.abs(ratio);
     const left = parseFloat(abs.toFixed(2)).toString(); 
     return `${sign}${left}:1`;
   }
  function getDayOfWeek(dateVal) {
    const d = parseDateVal(dateVal); 
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    return format(d, "EEEE");        
  }
  const dowMap = {};
  filteredRows.forEach((r) => {
    const dow = getDayOfWeek(r["Date"]);
    if (!dow) return;
    if (!dowMap[dow]) dowMap[dow] = { wins: 0, trades: 0 };
    dowMap[dow].trades++;
    if (r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true) {
      dowMap[dow].wins++;
    }
  });

  const sessionStats = useMemo(() => {
      const map = {};
      let best = "";
      let worst = "";
      let maxW = -1;
      let maxL = -1;

      filteredRows.forEach((r) => {
        const session = (r["Session"] || "").trim();
        if (!session) return;
        if (!map[session]) map[session] = { wins: 0, losses: 0, trades: 0 };
        map[session].trades++;
        
        const isW = r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true;
        const isL = r["Loss"] === "TRUE" || r["Loss"] === "Yes" || r["Loss"] === true;
        
        if (isW) map[session].wins++;
        if (isL) map[session].losses++;
      });

      Object.entries(map).forEach(([k, v]) => {
        if (v.wins > maxW) { maxW = v.wins; best = k; }
        if (v.losses > maxL) { maxL = v.losses; worst = k; }
      });

      return {
        best,
        worst,
        bestStats: map[best] || { wins: 0, losses: 0 },
        worstStats: map[worst] || { wins: 0, losses: 0 }
      };
    }, [filteredRows]);

  let bestDay = "",
    worstDay = "",
    bestDayWin = 0,
    worstDayWin = Infinity;
  Object.entries(dowMap).forEach(([k, v]) => {
    if (v.wins > bestDayWin) {
      bestDayWin = v.wins;
      bestDay = k;
    }
    if (v.wins < worstDayWin) {
      worstDayWin = v.wins;
      worstDay = k;
    }
  });

  const yesRules = filteredRows.filter(
    (r) => String(r["Did I follow my Rules?"]).toLowerCase() === "yes"
  ).length;
  const noRules = filteredRows.filter(
    (r) => String(r["Did I follow my Rules?"]).toLowerCase() === "no"
  ).length;
  const rainbowRulesData = [
    { name: "Yes", value: yesRules, fill: "#2563eb" },
    { name: "No", value: noRules, fill: "#FFFF00" },
  ];

  const followedRows = filteredRows.filter(
    (r) => String(r["Did I follow my Rules?"]).toLowerCase() === "yes"
  );
  const winWhenRules = followedRows.filter(
    (r) => r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true
  ).length;
  const lossWhenRules = followedRows.length - winWhenRules;
  const winRateWhenRules = followedRows.length ? Math.round((winWhenRules / followedRows.length) * 100) : 0;
  const rainbowWinWhenRulesData = [
    { name: "Wins", value: winWhenRules, fill: "#16a34a" },
    { name: "Losses", value: lossWhenRules, fill: "#FFFF00" },
  ];

  const dayGroups = {};
  filteredRows.forEach((r) => {
    if (!r["Date"]) return;
     const key = toKeyYMD(r["Date"]);
     if (!key) return;
     (dayGroups[key] ||= []).push(r);
  });

  const tradeOrderKey = (row) => {
    const n = parseNum(row["Trade #"]);
    if (n) return n; 

    const t = parseTimeTo24h(String(row["Exit Time"] ?? row["Entry Time"] ?? ""));
    return t ? t[0] * 60 + t[1] : 0; 
  };

  let bestDayPct = null;
  let bestDayPctDate = "";

  Object.entries(dayGroups).forEach(([dateKey, rows]) => {
    const sorted = rows.slice().sort((a, b) => tradeOrderKey(a) - tradeOrderKey(b));
    const first = sorted[0];
    if (!first) return;

    const dayStartBal =
      parseNum(first["Actual Balance"]) - parseNum(first["Actual Reward"]);

    const dayInc = rows.reduce((sum, r) => sum + parseNum(r["Actual Reward"]), 0);

    const pct = dayStartBal ? (dayInc / dayStartBal) * 100 : 0;

    if (bestDayPct == null || pct > bestDayPct) {
      bestDayPct = pct;
      bestDayPctDate = dateKey; 
    }
  });


  const bestProdStats = productWinMap[bestProduct] || { wins: 0, trades: 0 };
  const bestProdLosses = bestProdStats.trades - bestProdStats.wins;

  const rainbowBestProd = [
    { name: "Wins", value: bestProdStats.wins, fill: "#2563eb" },
    { name: "Losses", value: bestProdLosses, fill: "#FFFF00" },
  ];

  const worstProdStats = productWinMap[worstProduct] || { wins: 0, trades: 0 };
  const worstProdLosses = worstProdStats.trades - worstProdStats.wins;

  const rainbowWorstProd = [
    { name: "Wins", value: worstProdStats.wins, fill: "#2563eb" },
    { name: "Losses", value: worstProdLosses, fill: "#FFFF00" },
  ];

    const averageRiskToReward = useMemo(() => {
      let sum = 0, n = 0;
      filteredRows.forEach((row) => {
        const rr =
          parseRiskToReward(row["Actual Risk to Reward"]) ??
          parseRiskToReward(row["Risk/Reward"]);
        if (rr == null || !isFinite(rr)) return;  
  
        const pnl = parseNum(row["Actual Reward"]);
        if (!isFinite(pnl) || pnl === 0) return;             
  
        sum += pnl < 0 ? -Math.abs(rr) : Math.abs(rr);
        n += 1;
      });
      return n ? sum / n : null;
    }, [filteredRows]);
  const absRR = averageRiskToReward != null ? Math.abs(averageRiskToReward) : null;
  const formattedAverageRR = formatRiskToReward(averageRiskToReward);

  const winTrades = filteredRows.filter(
    (r) => r["Actual Reward"] > 0
  );
  const lossTrades = filteredRows.filter(
    (r) => r["Actual Reward"] < 0
  );

  const averageWinTrade = winTrades.length
    ? winTrades.reduce((acc, row) => acc + parseNum(row["Actual Reward"]), 0) /
      winTrades.length
    : 0;

  const averageLosingTrade = lossTrades.length
    ? lossTrades.reduce((acc, row) => acc + parseNum(row["Actual Reward"]), 0) /
      lossTrades.length
    : 0;

  const durationBuckets = [
    { label: "<1m", min: 0, max: 1 },
    { label: "1-2m", min: 1, max: 3 },
    { label: "3-5m", min: 3, max: 5 },
    { label: "5-15m", min: 5, max: 15 },
    { label: "15-30m", min: 15, max: 30 },
    { label: "30-60m", min: 30, max: 60 },
    { label: "1h+", min: 60, max: Infinity },
  ];

  const winsLossByTimeframe = useMemo(() => {
    const buckets = [
      { label: "12:00 am - 2:00 am", start: 0,   end: 2,   Wins: 0, Losses: 0 },
      { label: "2:00 am - 4:00 am",  start: 2,   end: 4,   Wins: 0, Losses: 0 },
      { label: "4:00 am - 6:00 am",  start: 4,   end: 6,   Wins: 0, Losses: 0 },
      { label: "6:00 am - 8:00 am",  start: 6,   end: 8,   Wins: 0, Losses: 0 },
      { label: "8:00 am - 10:00 am", start: 8,   end: 10,  Wins: 0, Losses: 0 },
      { label: "10:00 am - 12:00 pm",start: 10,  end: 12,  Wins: 0, Losses: 0 },
      { label: "12:00 pm - 2:00 pm", start: 12,  end: 14,  Wins: 0, Losses: 0 },
      { label: "2:00 pm - 4:00 pm",  start: 14,  end: 16,  Wins: 0, Losses: 0 },
      { label: "4:00 pm - 6:00 pm",  start: 16,  end: 18,  Wins: 0, Losses: 0 },
      { label: "6:00 pm - 8:00 pm",  start: 18,  end: 20,  Wins: 0, Losses: 0 },
      { label: "8:00 pm - 10:00 pm", start: 20,  end: 22,  Wins: 0, Losses: 0 },
      { label: "10:00 pm - 12:00 am",start: 22,  end: 24,  Wins: 0, Losses: 0 },
    ];

    const isWin  = (r) => r?.Win === true || r?.Win === "TRUE" || r?.Win === "Yes";
    const isLoss = (r) => r?.Loss === true || r?.Loss === "TRUE" || r?.Loss === "Yes";


    filteredRows.forEach((row) => {
      const entryTime = row["Entry Time"];
      if (!entryTime) return;

      let hour;
      if (entryTime.toLowerCase().includes("am") || entryTime.toLowerCase().includes("pm")) {
        const [time, ampm] = entryTime.split(" ");
        let [h] = time.split(":");
        h = parseInt(h, 10);
        if (ampm.toLowerCase() === "pm" && h !== 12) h += 12;
        if (ampm.toLowerCase() === "am" && h === 12) h = 0;
        hour = h;
      } else {
        const [h] = entryTime.split(":");
        hour = parseInt(h, 10);
      }

      const bucket = buckets.find(b => hour >= b.start && hour < b.end);
      if (bucket) {
        if (isWin(row)) bucket.Wins += 1;
        else if (isLoss(row)) bucket.Losses += 1;
      }
    });

    return buckets;
  }, [filteredRows]);

      const totalTimeframeTrades = useMemo(() => winsLossByTimeframe.reduce((acc, bucket) => acc + bucket.Wins + bucket.Losses, 0), [winsLossByTimeframe]);





      function money(v, currency = "USD") {
        try {
          if (typeof formatCurrency === "function") return formatCurrency(v, currency);
          return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v ?? 0);
        } catch {
          return (v ?? 0).toLocaleString();
        }
      }

      function isWin(v) {
        if (v === true) return true;
        const s = String(v).toLowerCase();
        return s === "true" || s === "yes";
      }

  function getDurationMins(val) {
    if (!val) return 0;
    let [h, m] = (val || '').split(":").map(Number);
    h = isNaN(h) ? 0 : h;
    m = isNaN(m) ? 0 : m;
    return h * 60 + m;
  }

  const durationCounts = durationBuckets.map((bucket) => {
    const count = filteredRows.filter(r => {
      const mins = getDurationMins(r["Duration (h:mm)"]);
      return mins >= bucket.min && mins < bucket.max;
    }).length;
    return { bucket: bucket.label, count };
  });

  const winRateCounts = durationBuckets.map((bucket) => {
    const inBucket = filteredRows.filter(r => {
      const mins = getDurationMins(r["Duration (h:mm)"]);
      return mins >= bucket.min && mins < bucket.max;
    });
    const winCount = inBucket.filter(r => r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true).length;
    return { bucket: bucket.label, count: inBucket.length ? Math.round((winCount / inBucket.length) * 100) : 0 };
  });

  const durationAnalysisData = useMemo(() => {
    return durationBuckets.map(bucket => {
      const rowsInBucket = filteredRows.filter(r => {
        const durationStr = getAndRecalculateDuration(r);
        const mins = getDurationMins2(durationStr);
        return mins >= bucket.min && mins < bucket.max;
      });
      const wins = rowsInBucket.filter(r => r["Win"] === "TRUE" || r["Win"] === "Yes" || r["Win"] === true).length;
      const losses = rowsInBucket.length - wins;
      
      return {
        bucket: bucket.label,
        count: rowsInBucket.length,
        wins: rowsInBucket.length ? Math.round((wins / rowsInBucket.length) * 100) : 0,
        losses: rowsInBucket.length ? Math.round((losses / rowsInBucket.length) * 100) : 0,
      };
    });
  }, [filteredRows]);

  const durationData = durationAnalysisData.map(({ bucket, count }) => ({ bucket, count }));
  const winRateData = durationAnalysisData.map(({ bucket, wins, losses }) => ({ bucket, wins, losses }));

  const isDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto min-h-screen">
        <h1 className="text-3xl font-extrabold text-blue-900 dark:text-blue-100 mb-6">Statistics & Analysis</h1>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={`${cardBase} animate-pulse`}>
              <Accent />
              <div className={`${cardHeader} w-24 h-4 bg-gray-200/70 dark:bg-gray-700/70 rounded`}></div>
              <div className="mt-4 w-16 h-8 bg-gray-200/70 dark:bg-gray-700/70 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

const hasAnyRows = journalRows.some(r => !isFundingRow(r) && !isDidNotTake(r["Position"])); 
const hasVisibleRows = filteredRows.length > 0;     

const noData = !loading && !hasAnyRows;             
const noResults = !loading && hasAnyRows && !hasVisibleRows; 




const addHeader = (pdf, pageNum) => {
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const margin = 10;
    const headerHeight = 20; 

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor('#003366'); 
    pdf.text('Trading Statistics & Analysis', pdfWidth / 2, margin + 5, { align: 'center' });

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor('#333333'); 
    pdf.text(`Page ${pageNum}`, pdfWidth - margin, margin + 5, { align: 'right' });

    pdf.setDrawColor(0, 51, 102); 
    pdf.line(margin, margin + 10, pdfWidth - margin, margin + 10);
    
    return headerHeight;
};


const downloadPDF = async () => {
    const input = contentRef.current;
    if (!input) {
      alert("Could not download PDF. Report content is missing.");
      return;
    }

    setIsGeneratingPdf(true);
    setPdfProgress(0);

    const clone = input.cloneNode(true);

    clone.querySelectorAll('[class*="dark:"]').forEach(el => {
      const raw = el.getAttribute('class') || '';
      const filtered = raw.split(/\s+/).filter(c => !c.startsWith('dark:')).join(' ').trim();
      if (filtered) el.setAttribute('class', filtered);
      else el.removeAttribute('class');
    });

    const rechartsSelectors = [
      '.recharts-cartesian-axis-tick-value', 
      '.recharts-legend-item-text',          
      '.recharts-label'                      
    ];
    clone.querySelectorAll(rechartsSelectors.join(', ')).forEach(el => {
      el.style.fill = '#374151'; 
    });
    clone.querySelectorAll('svg text').forEach(el => {
      if (!el.style.fill) {
        el.style.fill = '#374151';
      }
    });

    clone.querySelectorAll('details').forEach(el => el.setAttribute('open', ''));

    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0px';
    document.body.appendChild(clone);

    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        let pageNum = 1;
        const headerHeight = addHeader(pdf, pageNum);
        let yOnPage = headerHeight;
         const elementsToPrint = Array.from(clone.children).filter(
           el => el.id !== "ai-feedback" && el.id !== "summarytable"
         );

        for (let i = 0; i < elementsToPrint.length; i++) {
            const element = elementsToPrint[i];
            
            try {
                // We keep scale: 2 for crisp text, but compress the output
                const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
                
                // Change to JPEG and set quality to 75% (0.75)
                const imgData = canvas.toDataURL('image/jpeg', 0.75); 
                const ratio = canvas.width / canvas.height;
                const imgHeight = (pdfWidth - 2 * margin) / ratio;

                if (yOnPage + imgHeight > pdfHeight - margin) {
                    pdf.addPage();
                    pageNum++;
                    addHeader(pdf, pageNum);
                    yOnPage = headerHeight;
                }

                // Update the image format string here from 'PNG' to 'JPEG'
                pdf.addImage(imgData, 'JPEG', margin, yOnPage, pdfWidth - 2 * margin, imgHeight);
                yOnPage += imgHeight + 5;
            
            } catch (elementError) {
                console.error(`Skipping a section due to an error:`, elementError);
            }

            const progress = ((i + 1) / elementsToPrint.length) * 100;
            setPdfProgress(progress);
        }

        const dateString = format(new Date(), "yyyy-MM-dd");
        pdf.save(`trading_stats_${dateString}.pdf`);

    } catch (generalError) {
        console.error("A critical error occurred during PDF generation:", generalError);
        alert("Sorry, an unexpected error occurred while creating the PDF.");
    } finally {
        document.body.removeChild(clone);
        setIsGeneratingPdf(false);
    }
};

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const wins = payload.find(p => p.dataKey === 'Wins')?.value || 0;
      const losses = payload.find(p => p.dataKey === 'Losses')?.value || 0;

      return (
        <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
          <p className="font-bold text-sm mb-2">{`${label}`}</p>
          <p className="text-sm text-blue-400">{`Wins: ${wins}`}</p>
          <p className="text-sm text-yellow-300">{`Losses: ${losses}`}</p>
        </div>
      );
    }
    return null;
  };

  function parseTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return 0;

  const lowerTime = timeStr.toLowerCase();
  let [h, m] = [0, 0];

  if (lowerTime.includes('am') || lowerTime.includes('pm')) {
    const [time, period] = lowerTime.split(' ');
    [h, m] = time.split(':').map(Number);
    if (period === 'pm' && h < 12) h += 12;
    if (period === 'am' && h === 12) h = 0; 
  } else {
    [h, m] = timeStr.split(':').map(Number);
  }
  return (h || 0) * 60 + (m || 0);
}

const formatTimeWithAmPm = (time) => {
  if (!time) return "–";
  const s = String(time).trim();

  const ampmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([APap][Mm])$/);
  if (ampmMatch) {
    let [, h, m, ap] = ampmMatch;
    const hour12 = (parseInt(h, 10) % 12) || 12;
    return `${hour12}:${m} ${ap.toUpperCase()}`;
  }

  const hhmmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmmMatch) {
    let [, h, m] = hhmmMatch;
    const hour = parseInt(h, 10);
    if (isNaN(hour)) return s;
    const ap = hour >= 12 ? "PM" : "AM";
    const hour12 = (hour % 12) || 12;
    return `${hour12}:${m} ${ap}`;
  }

  return s;
};

  const truncateStrategy = (strategy) => {
    if (typeof strategy === 'string' && strategy.length > 10) {
      return `${strategy.slice(0, 10)}...`;
    }
    return strategy;
  };


  const TfCountsTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const wins   = payload.find(p => p.dataKey === "Wins")?.value ?? 0;
      const losses = payload.find(p => p.dataKey === "Losses")?.value ?? 0;
      return (
        <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
          <p className="font-bold text-sm mb-2">{label}</p>
          <p className="text-sm text-blue-400">Wins: {wins}</p>
          <p className="text-sm text-amber-300">Losses: {losses}</p>
        </div>
      );
    };

    const TfAvgTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const avgWin  = payload.find(p => p.dataKey === "AvgWin")?.value ?? 0;
      const avgLoss = payload.find(p => p.dataKey === "AvgLoss")?.value ?? 0;
      return (
        <div className="p-3 bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700">
          <p className="font-bold text-sm mb-2">{label}</p>
          <p className="text-sm text-blue-400">
            Avg Win: {isPrivacyMode ? "******" : formatterCurrency.format(avgWin || 0)}
          </p>
          <p className="text-sm text-amber-300">
            Avg Loss: {isPrivacyMode ? "******" : formatterCurrency.format(avgLoss || 0)}
          </p>
        </div>
      );
    };
    

    function clamp01(x) { return Math.max(0, Math.min(1, x)); }

    function isTruthyCheck(v) {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v > 0;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return ['yes', 'true', '1', 'on', 'y', '✓'].includes(s);
      }
      return false;
    }

    function computeChecklistRatio(doc) {
      if (!doc || typeof doc !== 'object') return 0;

      for (const k of ['percent','percentage','completion','percentComplete','percentageComplete']) {
        const v = doc[k];
        if (typeof v === 'number' && isFinite(v)) {
          return v <= 1 ? clamp01(v) : v <= 100 ? clamp01(v / 100) : 0;
        }
        if (typeof v === 'string') {
          const n = parseFloat(v);
          if (isFinite(n)) return n <= 1 ? clamp01(n) : n <= 100 ? clamp01(n / 100) : 0;
        }
      }

      const completed = Number(doc.completedCount ?? doc.checked ?? doc.complete ?? doc.done);
      const total = Number(doc.totalCount ?? doc.total ?? doc.count);
      if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
        return clamp01(completed / total);
      }

      if (doc.items && typeof doc.items === 'object' && !Array.isArray(doc.items)) {
        const vals = Object.values(doc.items);
        if (vals.length) {
          const total2 = vals.length;
          const checked2 = vals.filter(isTruthyCheck).length;
          return total2 ? clamp01(checked2 / total2) : 0;
        }
      }
      if (Array.isArray(doc.items) && doc.items.length) {
        const total2 = doc.items.length;
        const checked2 = doc.items.filter(isTruthyCheck).length;
        return total2 ? clamp01(checked2 / total2) : 0;
      }

      let total3 = 0, checked3 = 0;
      for (const [k, v] of Object.entries(doc)) {
        if (['userId','accountId','date','createdAt','updatedAt','_path','id'].includes(k)) continue;
        if (typeof v === 'boolean' || typeof v === 'number' || typeof v === 'string') {
          total3++;
          if (isTruthyCheck(v)) checked3++;
        }
      }
      return total3 ? clamp01(checked3 / total3) : 0;
    }


    function DayDetailModal({ date, onClose }) {
            
            const dayStats = useMemo(() => {
              if (!date) return null;

              const trades = (filteredRows || []) // <-- Now using filteredRows
                .filter(r => !isFundingRow(r) && !isDidNotTake(r["Position"])) 
                .filter(r => isSameDay(parseDateVal(r["Date"]), date));
              
              if (trades.length === 0) {
                return {
                  trades: [],
                  tradeCount: 0,
                  totalIncrease: 0,
                  pctIncrease: 0,
                  avgRR: 0,
                  totalTicks: 0,
                };
              }

              trades.sort((a, b) => parseTime(a['Entry Time']) - parseTime(b['Entry Time']));

              const tradeCount = trades.length;
              const totalIncrease = trades.reduce((acc, row) => acc + parseNum(row["Actual Reward"]), 0);

              const firstTrade = trades[0];
              const dayStartBalance = parseNum(firstTrade["Actual Balance"]) - parseNum(firstTrade["Actual Reward"]);
              const pctIncrease = pctOpenToClose(dayStartBalance, totalIncrease);
              
              let rrSum = 0, rrCount = 0;
              trades.forEach(row => {
                const rr = parseRiskToReward(row["Actual Risk to Reward"]) ?? parseRiskToReward(row["Risk/Reward"]);
                if (rr == null || !isFinite(rr)) return;
                const pnl = parseNum(row["Actual Reward"]);
                if (!isFinite(pnl) || pnl === 0) return;
                rrSum += pnl < 0 ? -Math.abs(rr) : Math.abs(rr);
                rrCount++;
              });
              const avgRR = rrCount ? rrSum / rrCount : 0;
              
              let totalTicks = 0;
              if (tickPipKey) {
                totalTicks = trades
                  .map(getSignedTickPip)
                  .filter(v => v != null)
                  .reduce((a, b) => a + b, 0);
              }

              return {
                trades,
                tradeCount,
                totalIncrease,
                pctIncrease,
                avgRR,
                totalTicks,
              };

            }, [date, filteredRows]);

            if (!dayStats) return null;

            return (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
              >
                <div
                  className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()} 
                >
                  <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                    <h3 className="text-xl font-bold text-blue-900 dark:text-blue-200">
                      Trades for {format(date, "MMMM d, yyyy")}
                    </h3>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label="Close modal"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <div className="flex-1 p-6 overflow-y-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                      <div className={cardBase}>
                        <div className={cardHeader}>Total Increase</div>
                        <div className={`text-2xl font-bold ${dayStats.totalIncrease >= 0 ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-300'}`}>
                          {isPrivacyMode ? "******" : formatCurrency(dayStats.totalIncrease)}
                        </div>
                      </div>
                      <div className={cardBase}>
                        <div className={cardHeader}>% Increase</div>
                        <div className={`text-2xl font-bold ${dayStats.pctIncrease >= 0 ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-300'}`}>
                          {isPrivacyMode ? "******" : formatPercent(dayStats.pctIncrease)}
                        </div>
                      </div>
                      <div className={cardBase}>
                        <div className={cardHeader}># Trades</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {dayStats.tradeCount}
                        </div>
                      </div>
                      <div className={cardBase}>
                        <div className={cardHeader}>Avg Reward:Risk</div>
                        <div className={`text-2xl font-bold ${dayStats.avgRR >= 0 ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-300'}`}>
                          {formatRiskToReward(dayStats.avgRR)}
                        </div>
                      </div>
                      {tickPipKey && (
                         <div className={cardBase}>
                          <div className={cardHeader}>Total {tickPipUnit}</div>
                          <div className={`text-2xl font-bold ${dayStats.totalTicks >= 0 ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-300'}`}>
                            {dayStats.totalTicks.toFixed(1)}
                          </div>
                        </div>
                      )}
                    </div>

                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Trade List</h4>
                    <div className="overflow-x-auto rounded-lg border dark:border-gray-700">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Product</th>
                            <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Position</th>
                            <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Time</th>
                            <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">P&L</th>
                            <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">R:R</th>
                            {tickPipKey && (
                              <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">{tickPipUnit}</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {dayStats.trades.length > 0 ? (
                            dayStats.trades.map((r, i) => (
                              <tr key={r.id || i} className="bg-white dark:bg-gray-800">
                                <td className="px-3 py-2 text-gray-900 dark:text-white">{shortProduct(r["Product"])}</td>
                                <td className="px-3 py-2 text-gray-900 dark:text-white">{r["Position"]}</td>
                                <td className="px-3 py-2 text-gray-900 dark:text-white">{toAmPm(r["Entry Time"])} - {toAmPm(r["Exit Time"])}</td>
                                
                                <td className={`px-3 py-2 font-medium ${parseNum(r["Actual Reward"]) >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-yellow-600 dark:text-yellow-300'}`}>
                                  {formatCurrency(r["Actual Reward"])}
                                </td>

                                <td className="px-3 py-2 text-gray-900 dark:text-white">{formatRiskToReward(parseRiskToReward(r["Actual Risk to Reward"]))}</td>
                                
                                {tickPipKey && (() => {
                                  const ticks = getSignedTickPip(r);
                                  const val = ticks?.toFixed(1) ?? '--';
                                  const color = ticks == null ? 'text-gray-900 dark:text-white' : (ticks >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-yellow-600 dark:text-yellow-300');
                                  return (
                                    <td className={`px-3 py-2 font-medium ${color}`}>
                                      {val}
                                    </td>
                                  );
                                })()}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={tickPipKey ? 6 : 5} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                                No trades found for this day.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
       <div className="min-h-screen lg:pl-56 xl:pl-60 relative z-0">
         <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <h1 className="mt-4 mb-6 text-3xl font-extrabold text-blue-900 dark:text-blue-100">Statistics & Analysis</h1>
          
         <div className="mt-4 flex items-center gap-2">
           {hasVisibleRows && (
             <button
               className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
               onClick={downloadPDF}
               disabled={isGeneratingPdf}
               aria-busy={isGeneratingPdf}
             >
               {isGeneratingPdf ? (
                 <span>Generating PDF...</span>
               ) : (
                 <>
                   <FaDownload />
                   <span>Download PDF</span>
                 </>
               )}
             </button>
           )}
           <button
             className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-800 dark:text-blue-200 bg-blue-100/80 dark:bg-gray-700/80 border border-blue-200 dark:border-gray-700 rounded-xl hover:bg-blue-200/90 dark:hover:bg-gray-600 transition whitespace-nowrap"
             onClick={() => setShowFilters(!showFilters)}
           >
             <FaFilter />
             <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
           </button>
         </div>
        </div>

        {isGeneratingPdf && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out" 
                style={{ width: `${pdfProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>



      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 rounded-2xl shadow-inner bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date (Custom From)</label>
            <input
              type="date"
              value={filters.customFrom}
              onChange={(e) => setFilters(f => ({ ...f, customFrom: e.target.value, dateFilter: "custom" }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date (Custom To)</label>
            <input
              type="date"
              value={filters.customTo}
              onChange={(e) => setFilters(f => ({ ...f, customTo: e.target.value, dateFilter: "custom" }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product</label>
            <select
              value={filters.productFilter}
              onChange={(e) => setFilters(f => ({ ...f, productFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              {filterOptions.products.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Result</label>
            <select
              value={filters.resultFilter}
              onChange={(e) => setFilters(f => ({ ...f, resultFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option>Won</option>
              <option>Loss</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rules</label>
            <select
              value={filters.rulesFilter}
              onChange={(e) => setFilters(f => ({ ...f, rulesFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value={BLANK}>(Blank/None)</option>
              {filterOptions.rules.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position</label>
            <select
              value={filters.positionFilter}
              onChange={(e) => setFilters(f => ({ ...f, positionFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              {filterOptions.positions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entry Strategy</label>
            <select
              value={filters.entryStrategyFilter}
              onChange={(e) => setFilters(f => ({ ...f, entryStrategyFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value={BLANK}>(Blank/None)</option>
              {filterOptions.entryStrategies.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Exit Strategy</label>
            <select
              value={filters.exitStrategyFilter}
              onChange={(e) => setFilters(f => ({ ...f, exitStrategyFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value={BLANK}>(Blank/None)</option>
              {filterOptions.exitStrategies.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Session</label>
            <select
              value={filters.sessionFilter}
              onChange={(e) => setFilters(f => ({ ...f, sessionFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value={BLANK}>(Blank/None)</option>
              {filterOptions.sessions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entry Time (From)</label>
            <input
              type="time"
              value={filters.entryTimeFrom}
              onChange={(e) => setFilters(f => ({ ...f, entryTimeFrom: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entry Time (To)</label>
            <input
              type="time"
              value={filters.entryTimeTo}
              onChange={(e) => setFilters(f => ({ ...f, entryTimeTo: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Day of the Week</label>
            <select
              value={filters.dayOfWeekFilter}
              onChange={(e) => setFilters(f => ({ ...f, dayOfWeekFilter: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                dateFilter: "all",
                customFrom: "",
                customTo: "",
                productFilter: "",
                resultFilter: "",
                rulesFilter: "",
                positionFilter: "",
                entryStrategyFilter: "",
                exitStrategyFilter: "",
              })}
              className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100/80 border border-blue-200 rounded-md hover:bg-blue-200/90 dark:text-blue-100 dark:bg-gray-700/80 dark:hover:bg-gray-600 dark:border-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}


      {isTrialing && (
        <div className="rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 p-8 mb-8 text-center">
          <FaExclamationTriangle className="text-4xl text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-xl font-bold text-blue-900 dark:text-blue-100">
            Trial Mode Restricted
          </div>
          <div className="text-base text-blue-800 dark:text-blue-200 mt-2">
            Advanced analytics and performance insights are available after your free trial.
          </div>
        </div>
      )}


{noData && !isTrialing && (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 p-6 mb-8 text-center">
    <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">No data yet</div>
    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
      Add your first trade to see stats and insights.
    </div>
  </div>
)}

{noResults && !isTrialing && (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 p-6 mb-8">
    <div>
      <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">No results match your filters</div>
      <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
        Try adjusting or clear your filters.
      </div>
    </div>
    <button
      onClick={() =>
        setFilters({
          dateFilter: "all",
          customFrom: "",
          customTo: "",
          productFilter: "",
          resultFilter: "",
          rulesFilter: "",
          positionFilter: "",
          entryStrategyFilter: "",
          exitStrategyFilter: "",
        })
      }
      className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 dark:text-blue-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-700"
    >
      Clear Filters
    </button>
  </div>
)}


{hasVisibleRows && !isTrialing && (
  <>
          <div id="stats-content" ref={contentRef}>

        <CollapsibleSection title="Overview & KPIs">
         <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        {/* Win % */}
        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Win %</div>
          <div className="relative flex flex-col items-center w-full">
            <RadialBarChart width={140} height={90} innerRadius="80%" outerRadius="100%" data={rainbowWinData} startAngle={180} endAngle={0}>
              <RadialBar dataKey="value" cornerRadius={12} />
            </RadialBarChart>
            <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-2xl text-black dark:text-white">
              {winPct}%
            </span>
          </div>
          <div className="flex justify-center gap-3 text-xs font-bold mt-2">
            <span className="text-blue-700 dark:text-blue-400">Wins: {winCount}</span>
            <span className="text-yellow-600 dark:text-yellow-300">Losses: {lossCount}</span>
          </div>
        </div>

{/* Overall */}
        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Overall</div>
          <div className="flex flex-col items-center text-sm h-full w-full">
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">Account Increase*: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatCurrency(overallIncrease)}
                </span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold"># Trades: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">{tradeCount}</span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">% Increase: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatPercent(overallPct)}
                </span>
            </span>
            {tickPipKey && (
              <>
                <span className="mt-1">
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Avg {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(overallAvgTickPip || 0).toFixed(1)}
                  </span>
                </span>
                <span>
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Total {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(overallTotalTickPip || 0).toFixed(1)}
                  </span>
                </span>
              </>
            )}            
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold"># Trading Days: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">{tradingDaysCount}</span>
            </span>
            
            {/* INDICATOR MOVED TO THE BOTTOM */}
            <div className="mt-auto pt-3">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 italic">
                {hasActiveFilters ? "*Trades only (funding excluded)" : "*Includes funding & payouts"}
              </span>
            </div>
          </div>
        </div>

        {/* Today */}
        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Today</div>
          <div className="flex flex-col items-center text-sm">
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">Today's Increase: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatCurrency(todayInc)}
                </span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold"># Trades: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">{todayCount}</span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">% Increase: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatPercent(todayPct)}
                </span>
            </span>
            {tickPipKey && (
              <>
                <span className="mt-1">
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Avg {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(todayAvgTickPip || 0).toFixed(1)}
                  </span>
                </span>
                <span>
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Total {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(todayTotalTickPip || 0).toFixed(1)}
                  </span>
                </span>
              </>
            )}

          </div>
        </div>

        {/* This Week */}
        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>This Week</div>
          <div className="flex flex-col items-center text-sm">
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">This Week's Increase: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatCurrency(weekInc)}
                </span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold"># Trades: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">{weekCount}</span>
            </span>
            <span>
              <span className="text-blue-800 dark:text-blue-400 font-bold">% Increase: </span>
              <span className="font-bold text-yellow-500 dark:text-yellow-300">
                  {isPrivacyMode ? "******" : formatPercent(weekPct)}
                </span>
            </span>

            {tickPipKey && (
              <>
                <span className="mt-1">
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Avg {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(weekAvgTickPip || 0).toFixed(1)}
                  </span>
                </span>
                <span>
                  <span className="text-blue-800 dark:text-blue-400 font-bold">Total {tickPipUnit}: </span>
                  <span className="font-bold text-yellow-500 dark:text-yellow-300">
                    {Number(weekTotalTickPip || 0).toFixed(1)}
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Number of Trades</div>
          <div className="text-3xl font-bold text-blue-800 dark:text-white">{tradeCount}</div>
        </div>

        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Best Product</div>
          <span className="text-2xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
            {bestProduct || "--"}
          </span>
          <div className="relative flex flex-col items-center w-full mt-3 mb-2">
            <div className="flex gap-2 text-xs font-bold">
              <span className="text-blue-700 dark:text-blue-400">Wins: {bestProdStats.wins}</span>
              <span className="text-yellow-500 dark:text-yellow-300">Losses: {bestProdLosses}</span>
            </div>
          </div>
        </div>

        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Worst Product</div>
          <span className="text-2xl font-extrabold text-yellow-500 dark:text-yellow-300 mt-3">
            {worstProduct || "--"}
          </span>
          <div className="relative flex flex-col items-center w-full mt-3 mb-2">
            <div className="flex gap-2 text-xs font-bold">
              <span className="text-blue-700 dark:text-blue-400">Wins: {worstProdStats.wins}</span>
              <span className="text-yellow-500 dark:text-yellow-300">Losses: {worstProdLosses}</span>
            </div>
          </div>
        </div>

        <div className={cardBase}>
          <Accent />
          <div className={`${cardHeader}`}>Avg Reward to Risk</div>
          <span className="text-2xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
            {averageRiskToReward != null ? formatRiskToReward(averageRiskToReward) : "--"}
          </span>
                    <p className="mt-auto pt-2 text-[11px] italic text-gray-500 dark:text-gray-400">
                      Does not include imported trades
                    </p>
        </div>
      </div>

                <div className="grid grid-cols-1 mb-8">
            <div className={`${cardBase} text-center p-6`}>
              <Accent />
              <h3 className="font-semibold text-xl text-blue-800 dark:text-blue-400 mb-2">
                Reward to Risk Analysis
              </h3>

               <p className="text-gray-700 dark:text-gray-300 text-base">
                 {averageRiskToReward != null && averageRiskToReward < 0 ? (
                   <>
                     Your average risk to reward is{" "}
                     <span className="font-bold text-red-500 dark:text-red-500">
                       {formatRiskToReward(averageRiskToReward)}
                     </span>. This suggests that, on average, your losses are larger than your wins. This is a good time to analyze your trade management. Are you cutting winners too early or letting losers run? Re-committing to a minimum 2:1 risk-to-reward on all new trades is the best way to fix this.
                   </>
                 ) : absRR >= 2 ? (
                    <>
                      Your average risk to reward is{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">{formatRiskToReward(absRR)}</span>. A risk reward of{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">2:1</span> or greater allows you to have a low win rate and still maintain profitability. Keep up the great work!
                    </>
                  ) : absRR > 1 && absRR < 2 ? (
                    <>
                      Your average risk to reward is{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">{formatRiskToReward(absRR)}</span>. That's greater than a{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">1:1</span> and less than <span className="font-bold text-yellow-500 dark:text-yellow-300">2:1</span>. With this risk to reward you can be profitable but you will have to work harder and maintain a high win rate of at least <span className="font-bold text-yellow-500 dark:text-yellow-300">60%</span>.
                    </>
                  ) : (
                    <>
                      Your average risk to reward is{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">{formatRiskToReward(absRR)}</span>, anything less than a{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">1:1</span> requires a very high win rate to be profitable. Maintaining a very high win rate is difficult to sustain. Aim for at least a{" "}
                      <span className="font-bold text-yellow-500 dark:text-yellow-300">2:1</span> risk to reward and you will be able to lose more than you win and remain profitable.
                    </>
                  )}
                </p>
      

            </div>
          </div>
              </CollapsibleSection>

          <CollapsibleSection title="Win/Loss Day Averages">
            {/* 1. THE "SO WHAT?" BANNER */}
            <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
              <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                <FaLightbulb className="text-yellow-500" />
                Daily Performance Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {dayAgg.avgWinDayPnL > Math.abs(dayAgg.avgLossDayPnL) ? (
                  <>
                    Your winning days (<strong className="text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(dayAgg.avgWinDayPnL)}</strong>) are larger than your losing days (<strong className="text-red-500 dark:text-red-400">{isPrivacyMode ? "******" : formatCurrency(dayAgg.avgLossDayPnL)}</strong>). You are successfully preserving capital when the market is difficult.
                    <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Keep your daily loss limits tight. Your current risk parameters are highly effective.</span>
                  </>
                ) : dayAgg.avgWinDayPnL > 0 ? (
                   <>
                    Your average losing day (<strong className="text-red-500 dark:text-red-400">{isPrivacyMode ? "******" : formatCurrency(dayAgg.avgLossDayPnL)}</strong>) is wiping out the progress of your winning days (<strong className="text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(dayAgg.avgWinDayPnL)}</strong>).
                    <span className="block mt-2 font-semibold text-red-600 dark:text-red-400">Action: Implement a hard daily stop loss rule. If you drop below your average win day amount, walk away from the screens.</span>
                  </>
                ) : (
                   <>Not enough data yet to compare your average daily wins and losses.</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Avg Win Day PnL</div>
                <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {dayAgg.winDayCount ? (isPrivacyMode ? "******" : formatCurrency(dayAgg.avgWinDayPnL)) : "--"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {dayAgg.winDayCount} win day{dayAgg.winDayCount === 1 ? "" : "s"}
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Avg # Trades on Win Days</div>
                <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {dayAgg.winDayCount ? dayAgg.avgTradesOnWinDays.toFixed(1) : "--"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  trades per win day
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Avg Losing Day PnL</div>
                <div className="text-2xl font-extrabold text-yellow-600 dark:text-yellow-300 mt-3">
                  {dayAgg.lossDayCount ? (isPrivacyMode ? "******" : formatCurrency(dayAgg.avgLossDayPnL)) : "--"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {dayAgg.lossDayCount} losing day{dayAgg.lossDayCount === 1 ? "" : "s"}
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Avg # Trades on Losing Days</div>
                <div className="text-2xl font-extrabold text-yellow-600 dark:text-yellow-300 mt-3">
                  {dayAgg.lossDayCount ? dayAgg.avgTradesOnLossDays.toFixed(1) : "--"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  trades per losing day
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Buys Win %</div>
                <div className="relative flex flex-col items-center w-full">
                  <RadialBarChart
                    width={110}
                    height={70}
                    innerRadius="80%"
                    outerRadius="100%"
                    data={[
                      { name: "Wins", value: buyWinCount, fill: "#2563eb" },
                      { name: "Losses", value: buyCount - buyWinCount, fill: "#FFFF00" },
                    ]}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar dataKey="value" cornerRadius={12}>
                      <Cell fill="#2563eb" />
                      <Cell fill="#FFFF00" />
                    </RadialBar>
                  </RadialBarChart>
                  <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-lg text-black dark:text-white">
                    {buyCount ? Math.round((buyWinCount / buyCount) * 100) : 0}%
                  </span>
                </div>
                <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                  <span className="text-blue-700 dark:text-blue-400">Wins: {buyWinCount}</span>
                  <span className="text-yellow-500 dark:text-yellow-300">Losses: {buyCount - buyWinCount}</span>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Sells Win %</div>
                <div className="relative flex flex-col items-center w-full">
                  <RadialBarChart
                    width={110}
                    height={70}
                    innerRadius="80%"
                    outerRadius="100%"
                    data={[
                      { name: "Wins", value: sellWinCount, fill: "#2563eb" },
                      { name: "Losses", value: sellCount - sellWinCount, fill: "#FFFF00" },
                    ]}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar dataKey="value" cornerRadius={12}>
                      <Cell fill="#2563eb" />
                      <Cell fill="#FFFF00" />
                    </RadialBar>
                  </RadialBarChart>
                  <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-lg text-black dark:text-white">
                    {sellCount ? Math.round((sellWinCount / sellCount) * 100) : 0}%
                  </span>
                </div>
                <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                  <span className="text-blue-700 dark:text-blue-400">Wins: {sellWinCount}</span>
                  <span className="text-yellow-500 dark:text-yellow-300">Losses: {sellCount - sellWinCount}</span>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Longest Trade</div>
                <span className="text-xl font-extrabold text-blue-600 dark:text-blue-400 mt-3">
                  {longestTrade.dur ? formatDuration(longestTrade.dur) : "--"}
                </span>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Shortest Trade</div>
                <span className="text-xl font-extrabold text-yellow-500 dark:text-yellow-300 mt-3">
                  {shortestTrade.dur ? formatDuration(shortestTrade.dur) : "--"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
              <div className={`${cardBase} col-span-1 sm:col-span-4`}>
                <Accent />
                <div className={`${cardHeader}`}>Quick Trades (≤ 2 min apart)</div>
                <div className="text-3xl font-bold text-blue-800 dark:text-yellow-300 mt-2">
                  {quickSuccessionTrades}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Trades made within 2 minutes of the previous trade. A high number of quick trades is an indication of impulsive trading.
                </p>
              </div>
            </div>
          </CollapsibleSection>



             {!isOptionsOnlyContext && (
              <CollapsibleSection title="Stop Loss Analysis">
                {/* 1. THE "SO WHAT?" BANNER */}
                <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
                  <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                    <FaLightbulb className="text-yellow-500" />
                    Risk Execution Analysis
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {stopLossStats.avgLossSL > stopLossStats.avgWinSL ? (
                       <>
                         Your average stop loss size on losing trades (<strong>{stopLossStats.avgLossSL.toFixed(1)}</strong>) is larger than the stop loss size you set on winning trades (<strong>{stopLossStats.avgWinSL.toFixed(1)}</strong>).
                         <span className="block mt-2 font-semibold text-red-600 dark:text-red-400">Action: You may be moving your stop loss further away when a trade goes against you. Honor your initial hard stop.</span>
                       </>
                    ) : stopLossStats.avgWinSL > 0 ? (
                       <>
                         You maintain consistent or tighter risk parameters on your losing trades compared to your winning setups.
                         <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Continue strictly honoring your hard stops. Excellent risk management.</span>
                       </>
                    ) : (
                       <>Log your Stop Loss metrics on your journal entries to see if you are taking on excess risk during losing trades.</>
                    )}
                  </p>
                </div>

                <div className={`grid grid-cols-1 sm:grid-cols-2 ${isBacktesting ? "lg:grid-cols-4" : "lg:grid-cols-2"} gap-6 mb-8`}>
                  <div className={cardBase}>
                    <Accent />
                    <div className={cardHeader}>Avg Stop Loss (Wins)</div>
                    <div className="text-2xl font-extrabold text-blue-600 dark:text-blue-400 mt-3">
                      {stopLossStats.avgWinSL ? stopLossStats.avgWinSL.toFixed(1) : "--"}
                    </div>
                  </div>

                  {hasIdealSLData && (
                    <div className={cardBase}>
                      <Accent />
                      <div className={cardHeader}>Avg Ideal SL (Wins)</div>
                      <div className="text-2xl font-extrabold text-green-500 dark:text-green-400 mt-3">
                        {stopLossStats.avgWinIdeal ? stopLossStats.avgWinIdeal.toFixed(1) : "--"}
                      </div>
                    </div>
                  )}

                  <div className={cardBase}>
                    <Accent />
                    <div className={cardHeader}>Avg Stop Loss (Losses)</div>
                    <div className="text-2xl font-extrabold text-yellow-600 dark:text-yellow-400 mt-3">
                      {stopLossStats.avgLossSL ? stopLossStats.avgLossSL.toFixed(1) : "--"}
                    </div>
                  </div>

                  {hasIdealSLData && (
                    <div className={cardBase}>
                      <Accent />
                      <div className={cardHeader}>Avg Ideal SL (Losses)</div>
                      <div className="text-2xl font-extrabold text-red-600 dark:text-red-400 mt-3">
                        {stopLossStats.avgLossIdeal ? stopLossStats.avgLossIdeal.toFixed(1) : "--"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6 mb-8">
                  <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
                    Average Stop Loss For Wins vs. Losses By Time Period
                  </div>
                  <div className="h-[360px] flex flex-col">
                    {stopLossStats.chartData.some(d => 
                      d["Win Actual"] > 0 || d["Win Ideal"] > 0 || d["Loss Actual"] > 0 || d["Loss Ideal"] > 0
                    ) ? (
                      <>
                        {/* NEW LEGEND */}
                        <div className="flex flex-wrap items-center justify-center gap-6 text-xs mb-2 text-gray-600 dark:text-gray-300">
                          <span className="flex items-center gap-1">
                            <span className="text-red-500 font-bold text-lg leading-none">↑</span> Ideal SL &gt; Actual (Stop loss may need to be bigger)
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="text-green-500 font-bold text-lg leading-none">↓</span> Ideal SL &lt; Actual (Stop loss may be too big)
                          </span>
                        </div>

                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={stopLossStats.chartData} margin={{ top: 25, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                            <XAxis dataKey="name" stroke={isDark ? "#94a3b8" : "#6b7280"} tick={{ fontSize: 12 }} interval={0} angle={-45} textAnchor="end" height={60} />
                            <YAxis stroke={isDark ? "#94a3b8" : "#6b7280"} />
                            <Tooltip content={<SLTimeTooltip />} cursor={{ fill: isDark ? "rgba(100,116,139,.2)" : "rgba(203,213,225,.4)" }} />
                            <Legend formatter={renderLegendText} wrapperStyle={{ paddingTop: "20px" }} />
                            
                            <Bar dataKey="Win Actual" fill="#2563eb" name="Win (Actual)" radius={[4, 4, 0, 0]} />
                            {hasIdealSLData && (
                              <Bar dataKey="Win Ideal" fill="#22c55e" name="Win (Ideal)" radius={[4, 4, 0, 0]}>
                                <LabelList content={renderStopLossIndicator("Win Actual", "Win Ideal", stopLossStats.chartData)} />
                              </Bar>
                            )}
                            
                            <Bar dataKey="Loss Actual" fill="#eab308" name="Loss (Actual)" radius={[4, 4, 0, 0]} />
                            {hasIdealSLData && (
                              <Bar dataKey="Loss Ideal" fill="#ef4444" name="Loss (Ideal)" radius={[4, 4, 0, 0]}>
                                <LabelList content={renderStopLossIndicator("Loss Actual", "Loss Ideal", stopLossStats.chartData)} />
                              </Bar>
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                        No Stop Loss data available for these time periods.
                      </div>
                    )}
                  </div>
                </div>
              </CollapsibleSection>
             )}

          <CollapsibleSection title="Best Days and Trades">
            {/* 1. THE "SO WHAT?" BANNER */}
            <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
              <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                <FaLightbulb className="text-yellow-500" />
                Performance Timing Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {bestDay ? (
                  <>Your most profitable day of the week is <strong>{bestDay}</strong>. </>
                ) : ""}
                {averageWinTrade > Math.abs(averageLosingTrade) ? (
                  <>
                    You do a great job keeping your average win (<strong className="text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(averageWinTrade)}</strong>) larger than your average loss (<strong className="text-red-500 dark:text-red-400">{isPrivacyMode ? "******" : formatCurrency(averageLosingTrade)}</strong>).
                    <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Capitalize on {bestDay} setups, as your math supports scaling up your position size.</span>
                  </>
                ) : averageWinTrade > 0 ? (
                  <>
                    Your average loss (<strong className="text-red-500 dark:text-red-400">{isPrivacyMode ? "******" : formatCurrency(averageLosingTrade)}</strong>) is currently larger than your average win (<strong className="text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(averageWinTrade)}</strong>).
                    <span className="block mt-2 font-semibold text-yellow-700 dark:text-yellow-400">Action: Review your risk management. You must start trimming losing trades earlier to protect your capital.</span>
                  </>
                ) : (
                  <>Not enough data yet to compare your average wins and losses.</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Best Day of the Week</div>
                <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {bestDay}
                </span>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Worst Day of the Week</div>
                <span className="text-xl font-extrabold text-yellow-500 dark:text-yellow-300 mt-3">
                  {worstDay}
                </span>
              </div>

              <div className={cardBase}>
                  <Accent />
                  <div className={`${cardHeader}`}>Best Session</div>
                  <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                    {sessionStats.best || "--"}
                  </span>
                  {sessionStats.best && (
                    <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                      <span className="text-blue-700 dark:text-blue-400">Wins: {sessionStats.bestStats.wins}</span>
                      <span className="text-yellow-600 dark:text-yellow-300">Losses: {sessionStats.bestStats.losses}</span>
                    </div>
                  )}
                </div>

                <div className={cardBase}>
                  <Accent />
                  <div className={`${cardHeader}`}>Worst Session</div>
                  <span className="text-xl font-extrabold text-yellow-500 dark:text-yellow-300 mt-3">
                    {sessionStats.worst || "--"}
                  </span>
                  {sessionStats.worst && (
                    <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                      <span className="text-blue-700 dark:text-blue-400">Wins: {sessionStats.worstStats.wins}</span>
                      <span className="text-yellow-600 dark:text-yellow-300">Losses: {sessionStats.worstStats.losses}</span>
                    </div>
                  )}
                </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Best Day % Increase</div>
                <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {bestDayPct ? `${formatPercent(bestDayPct)} (${formatDateUS(bestDayPctDate)})` : "--"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Best Trade</div>
                <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {bestTrade.row
                    ? `${isPrivacyMode ? "******" : formatCurrency(bestTrade.rew)} (${formatDateUS(bestTrade.row["Date"] || "")})`
                    : "--"}
                </span>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Worst Trade</div>
                <span className="text-xl font-extrabold text-yellow-600 dark:text-yellow-300 mt-3">
                  {worstTrade.row
                    ? `${isPrivacyMode ? "******" : formatCurrency(worstTrade.rew)} (${formatDateUS(worstTrade.row["Date"] || "")})`
                    : "--"}
                </span>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Average Win Trade</div>
                <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400 mt-3">
                  {averageWinTrade ? (isPrivacyMode ? "******" : formatCurrency(averageWinTrade)) : "--"}
                </span>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Average Losing Trade</div>
                <span className="text-xl font-extrabold text-yellow-500 dark:text-yellow-300 mt-3">
                  {averageLosingTrade ? (isPrivacyMode ? "******" : formatCurrency(averageLosingTrade)) : "--"}
                </span>
              </div>
            </div>
          </CollapsibleSection>



 <CollapsibleSection title="Rules & Consistency">
            
            {/* 1. THE "SO WHAT?" BANNER */}
            <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
              <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                <FaLightbulb className="text-yellow-500" />
                Discipline Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {yesRules > noRules && winRateWhenRules > 50 ? (
                  <>
                    You are highly disciplined, following your rules <strong>{yesRules} times</strong> compared to {noRules} violations. 
                    More importantly, your strategy works: you win <strong>{winRateWhenRules}%</strong> of the time when you stick to the plan. 
                    <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Scale up your size. Your edge is proven and your discipline is intact.</span>
                  </>
                ) : yesRules > noRules && winRateWhenRules <= 50 ? (
                  <>
                    You have great discipline ({yesRules} rules followed vs {noRules} broken), but your win rate is only <strong>{winRateWhenRules}%</strong> when executing perfectly. 
                    <span className="block mt-2 font-semibold text-yellow-700 dark:text-yellow-400">Action: Your execution is fine, but your system is leaking. Review your entry criteria.</span>
                  </>
                ) : (
                  <>
                    You are breaking your rules more often than you follow them ({noRules} violations vs {yesRules} followed). This makes your actual win rate mathematically irrelevant, as you are trading on impulse rather than a tested system.
                    <span className="block mt-2 font-semibold text-red-600 dark:text-red-400">Action: Cut your position size in half until you can execute your rules for 5 consecutive trades.</span>
                  </>
                )}
              </p>
            </div>

            {/* 2. THE EVIDENCE */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Followed Rules</div>
                <div className="relative flex flex-col items-center w-full">
                  <RadialBarChart
                    width={110}
                    height={70}
                    innerRadius="80%"
                    outerRadius="100%"
                    data={rainbowRulesData}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar dataKey="value" cornerRadius={12}>
                      {rainbowRulesData.map((entry, index) => (
                        <Cell key={`cell-frules-${index}`} fill={entry.fill} />
                      ))}
                    </RadialBar>
                  </RadialBarChart>
                  <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-lg text-black dark:text-white">
                    {tradeCount ? Math.round((yesRules / tradeCount) * 100) : 0}%
                  </span>
                </div>
                <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                  <span className="text-blue-700 dark:text-blue-400">Yes: {yesRules}</span>
                  <span className="text-yellow-500 dark:text-yellow-300">No: {noRules}</span>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Win Rate (Followed Rules)</div>
                <div className="relative flex flex-col items-center w-full">
                  <RadialBarChart
                    width={110}
                    height={70}
                    innerRadius="80%"
                    outerRadius="100%"
                    data={[
                      { name: "Wins", value: winWhenRules, fill: "#2563eb" },
                      { name: "Losses", value: lossWhenRules, fill: "#FFFF00" },
                    ]}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar dataKey="value" cornerRadius={12}>
                      <Cell fill="#2563eb" />
                      <Cell fill="#FFFF00" />
                    </RadialBar>
                  </RadialBarChart>
                  <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-lg text-black dark:text-white">
                    {followedRows.length
                      ? Math.round((winWhenRules / followedRows.length) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                  <span className="text-blue-700 dark:text-blue-400">Wins: {winWhenRules}</span>
                  <span className="text-yellow-500 dark:text-yellow-300">Losses: {lossWhenRules}</span>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Loss Rate (Followed Rules)</div>
                <div className="relative flex flex-col items-center w-full">
                  <RadialBarChart
                    width={110}
                    height={70}
                    innerRadius="80%"
                    outerRadius="100%"
                    data={[
                      { name: "Losses", value: lossWhenRules, fill: "#FFFF00" },
                      { name: "Wins", value: winWhenRules, fill: "#2563eb" },
                    ]}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar dataKey="value" cornerRadius={12}>
                      <Cell fill="#FFFF00" />
                      <Cell fill="#2563eb" />
                    </RadialBar>
                  </RadialBarChart>
                  <span className="absolute left-1/2 top-[44%] transform -translate-x-1/2 -translate-y-1/2 font-extrabold text-lg text-black dark:text-white">
                    {followedRows.length
                      ? Math.round((lossWhenRules / followedRows.length) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="flex justify-center gap-3 text-xs font-bold mt-2">
                  <span className="text-blue-700 dark:text-blue-400">Wins: {winWhenRules}</span>
                  <span className="text-yellow-500 dark:text-yellow-300">Losses: {lossWhenRules}</span>
                </div>
              </div>
            </div>
          </CollapsibleSection>


     



          <CollapsibleSection title="Mood & Performance">
            {/* 1. THE "SO WHAT?" BANNER */}
            <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
              <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                <FaLightbulb className="text-yellow-500" />
                Psychology Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {moodTradeCount > 0 ? (
                  <>
                    {winRatePositive >= winRateNeutral && winRatePositive >= winRateNegative ? (
                      <>You perform best when you are in a <strong>Positive</strong> mood, boasting a win rate of <strong>{winRatePositive}%</strong>. Maintaining a good headspace is critical to your edge.</>
                    ) : winRateNegative > winRatePositive && winRateNegative > winRateNeutral ? (
                      <>Interestingly, your win rate is highest (<strong>{winRateNegative}%</strong>) when in a <strong>Negative</strong> mood. </>
                    ) : (
                      <>Your performance is highest when you are in a <strong>Neutral</strong> mood, with a win rate of <strong>{winRateNeutral}%</strong>. Objective, emotionless execution seems to be your strongest edge.</>
                    )}
                    
                    {negMood45Count > posMood12Count && negMood45Count > neutralMood3Count ? (
                       <span className="block mt-2 font-semibold text-red-600 dark:text-red-400">Action: A significant portion of your trades are taken in a negative mood. Step away from the charts when frustrated to protect your capital.</span>
                    ) : (
                       <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Ensure you are in your optimal headspace before clicking buy or sell today.</span>
                    )}
                  </>
                ) : (
                  <>Log your Mood/Mindset on your trades to discover how your psychology affects your win rate and profitability.</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader} mb-4`}>Mood Distribution</div>
                <div className="w-full grid grid-cols-3 gap-4">
                  <div className="flex flex-col items-center">
                    <div className="font-semibold text-sm text-blue-500 dark:text-blue-400">Positive</div>
                    <div className="relative h-16 w-16">
                      <RadialBarChart width={64} height={64} innerRadius="70%" outerRadius="100%" data={[{ value: moodTradeCount ? (posMood12Count / moodTradeCount) * 100 : 0 }]} startAngle={90} endAngle={-270}>
                        <RadialBar dataKey="value" fill="#2563eb" cornerRadius={10} background={{ fill: isDark ? '#374151' : '#e5e7eb' }} />
                      </RadialBarChart>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-black dark:text-white">
                        {moodTradeCount ? Math.round((posMood12Count / moodTradeCount) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">{posMood12Count} trades</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="font-semibold text-sm text-gray-500 dark:text-gray-400">Neutral</div>
                    <div className="relative h-16 w-16">
                      <RadialBarChart width={64} height={64} innerRadius="70%" outerRadius="100%" data={[{ value: moodTradeCount ? (neutralMood3Count / moodTradeCount) * 100 : 0 }]} startAngle={90} endAngle={-270}>
                        <RadialBar dataKey="value" fill="#6b7280" cornerRadius={10} background={{ fill: isDark ? '#374151' : '#e5e7eb' }} />
                      </RadialBarChart>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-black dark:text-white">
                        {moodTradeCount ? Math.round((neutralMood3Count / moodTradeCount) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">{neutralMood3Count} trades</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="font-semibold text-sm text-yellow-500 dark:text-yellow-300">Negative</div>
                    <div className="relative h-16 w-16">
                      <RadialBarChart width={64} height={64} innerRadius="70%" outerRadius="100%" data={[{ value: moodTradeCount ? (negMood45Count / moodTradeCount) * 100 : 0 }]} startAngle={90} endAngle={-270}>
                        <RadialBar dataKey="value" fill="#FFFF00" cornerRadius={10} background={{ fill: isDark ? '#374151' : '#e5e7eb' }} />
                      </RadialBarChart>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-black dark:text-white">
                        {moodTradeCount ? Math.round((negMood45Count / moodTradeCount) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">{negMood45Count} trades</div>
                  </div>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Performance by Mood</div>
                <div className="w-full flex-grow flex items-center">
                  <div className="w-full grid grid-cols-3 items-center gap-y-4 gap-x-2 px-2">
                    <span className="font-semibold text-sm text-gray-600 dark:text-gray-300">When Positive</span>
                    <div className="text-center">
                      <div className="font-bold text-lg text-blue-500 dark:text-blue-400">{winRatePositive}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Win</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg text-yellow-500 dark:text-yellow-300">{lossRatePositive}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Loss</div>
                    </div>

                    <span className="font-semibold text-sm text-gray-600 dark:text-gray-300">When Neutral</span>
                    <div className="text-center">
                      <div className="font-bold text-lg text-gray-500 dark:text-gray-400">{winRateNeutral}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Win</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg text-gray-500 dark:text-gray-400">{lossRateNeutral}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Loss</div>
                    </div>

                    <span className="font-semibold text-sm text-gray-600 dark:text-gray-300">When Negative</span>
                    <div className="text-center">
                      <div className="font-bold text-lg text-blue-500 dark:text-blue-400">{winRateNegative}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Win</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg text-yellow-500 dark:text-yellow-300">{lossRateNegative}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Loss</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={cardBase}>
                <Accent />
                <div className={`${cardHeader}`}>Average Grade</div>
                <div className="text-5xl font-extrabold text-blue-800 dark:text-blue-400 mt-4">
                  {gradeStats.average || "--"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Based on {gradeStats.count} graded trades
                </div>
              </div>
            </div>
          </CollapsibleSection>





<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6 h-full">
    <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
      Daily Account Balance
    </div>
    <div className="h-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={accountBalanceData}>
          <CartesianGrid
            stroke={typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "#374151" : "#e5e7eb"}
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            padding={{ left: 20, right: 20 }}
            tick={{
              fill: typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "#fff" : "#222",
              fontWeight: 500,
              dy: 8,
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={isPrivacyMode ? () => "***" : undefined} // <-- ADD THIS LINE
            tick={{
              fill: typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "#fff" : "#222",
              fontWeight: 500,
            }}
          />
          <ReferenceLine 
            y={startingBalance} 
            stroke={isDark ? "#475569" : "#cbd5e1"} 
            label={{ 
              position: 'top', 
              value: 'Breakeven', 
              fill: isDark ? "#94a3b8" : "#6b7280", 
              fontSize: 12 
            }} 
          />
          <Tooltip
            formatter={isPrivacyMode ? () => ["***", "Balance"] : undefined} // <-- ADD THIS LINE
            contentStyle={{ background: "#1e293b", color: "#fff", border: "none" }}
            labelStyle={{ color: "#fff" }}
            wrapperStyle={{ zIndex: 99 }}
          />
          <Line
            type="monotone"
            dataKey="balance"
            stroke="#2563eb"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 6, fill: "#2563eb", stroke: "#fff", strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6 h-full">
    <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
      Wins / Losses by Time Period
    </div>
    <div className="h-[360px]">
      {totalTimeframeTrades > 0 ? (
         <ResponsiveContainer width="100%" height="100%">
           <BarChart
             data={winsLossByTimeframe}
             layout="vertical"
             /* Updated right margin to 40 */
             margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
           >
             <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
             <XAxis
               type="number"
               allowDecimals={false}
               stroke={isDark ? "#94a3b8" : "#6b7280"}
               domain={[0, (dataMax) => Math.max(1, dataMax)]}  
             />
            <YAxis
              type="category"
              dataKey="label"
              interval={0}  
              stroke={isDark ? "#94a3b8" : "#6b7280"}
              width={200}
              tick={{ fontSize: 14 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
            />
            <Legend formatter={renderLegendText} iconType="circle" wrapperStyle={{ paddingBottom: '10px' }} />
            
            <Bar dataKey="Wins" stackId="a" fill="#2563eb" barSize={18} radius={[4, 0, 0, 4]} />
            
            <Bar dataKey="Losses" stackId="a" fill="#FFFF00" barSize={18} radius={[0, 4, 4, 0]}>
              {/* Attached new total count label here */}
              <LabelList content={renderTotalCountLabel("Wins", "Losses", winsLossByTimeframe)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
          No timeframe data available
        </div>
      )}
    </div>
  </div>

</div>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
              <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
                Trade Duration Analysis
              </div>
              <ResponsiveContainer width="100%" height={260}>
                {/* Added right margin to fit the label */}
                <BarChart data={durationData} layout="vertical" margin={{ top: 0, right: 40, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                  <XAxis type="number" allowDecimals={false} stroke={isDark ? "#94a3b8" : "#6b7280"} axisLine={false} tickLine={false} />
                  <YAxis dataKey="bucket" type="category" width={120} stroke={isDark ? "#94a3b8" : "#6b7280"} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: isDark ? "#1e293b" : "#fff", color: isDark ? "#e0e7ef" : "#111" }} 
                            cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
                  />
                  <Bar dataKey="count" fill="#2563eb" barSize={18} radius={[4, 4, 4, 4]}>
                    {/* Native Recharts LabelList */}
                    <LabelList dataKey="count" position="right" fill={isDark ? "#94a3b8" : "#6b7280"} fontSize={12} fontWeight="bold" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>


            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
              <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
                Win Rate by Trade Duration
              </div>
              <ResponsiveContainer width="100%" height={260}>
                {/* Added right margin to fit the label */}
                <BarChart data={winRateData} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                  <XAxis type="number" allowDecimals={false} stroke={isDark ? "#94a3b8" : "#6b7280"} axisLine={false} tickLine={false} />
                  <YAxis dataKey="bucket" type="category" width={120} stroke={isDark ? "#94a3b8" : "#6b7280"} axisLine={false} tickLine={false} />
                  <Tooltip 
                    formatter={(value) => `${value}%`}
                    contentStyle={{ background: isDark ? "#1e293b" : "#fff", color: isDark ? "#e0e7ef" : "#111" }}
                    cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
                  />
                  <Bar dataKey="wins" stackId="a" fill="#2563eb" barSize={18} radius={[4, 0, 0, 4]} />
                  
                  {/* Attached LabelList to the second bar in the stack */}
                  <Bar dataKey="losses" stackId="a" fill="#FFFF00" barSize={18} radius={[0, 4, 4, 0]}>
                    <LabelList content={renderWinPctValueLabel("wins", "losses", winRateData)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-8">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
              <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
                Wins / Losses by Session
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={winsLossBySession}
                  layout="vertical"
                  margin={{ top: 10, right: 40, left: 10, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                  <YAxis
                    type="category"
                    dataKey="session"
                    width={110}
                    stroke={isDark ? "#94a3b8" : "#6b7280"}
                    axisLine={false} 
                    tickLine={false}
                  />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    stroke={isDark ? "#94a3b8" : "#6b7280"}
                    axisLine={false} 
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? "#1e293b" : "#fff",
                      color: isDark ? "#e0e7ef" : "#111"
                    }}
                    cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
                  />
                  <Legend formatter={renderLegendText} iconType="circle"  />
                  <Bar dataKey="Wins" stackId="a" fill="#2563eb" barSize={18} radius={[4, 0, 0, 4]} />
                  <Bar dataKey="Losses" stackId="a" fill="#FFFF00" barSize={18} radius={[0, 4, 4, 0]}>
                                <LabelList content={renderWinPercentageLabel("Wins", "Losses", winsLossBySession)} />
                              </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
                    <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
                      Wins / Losses by Entry Strategy
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={winsLossByEntry.map((item) => ({
                          ...item,
                          strategy: item.strategy?.trim() || "Unknown"
                        }))}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                        <YAxis
                          type="category"
                          dataKey="strategy"
                          width={110}
                          stroke={isDark ? "#94a3b8" : "#6b7280"}
                          axisLine={false} 
                          tickLine={false}
                          tickFormatter={truncateStrategy}
                        />
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          stroke={isDark ? "#94a3b8" : "#6b7280"}
                          axisLine={false} 
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: isDark ? "#1e293b" : "#fff",
                            color: isDark ? "#e0e7ef" : "#111"
                          }}
                          cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
                        />
                        <Legend formatter={renderLegendText} iconType="circle" />
                              <Bar dataKey="Wins" stackId="a" fill="#2563eb" barSize={18} radius={[4, 0, 0, 4]} />

                              <Bar dataKey="Losses" stackId="a" fill="#FFFF00" barSize={18} radius={[0, 4, 4, 0]}>
                                <LabelList content={renderWinPercentageLabel("Wins", "Losses", winsLossByEntry)} />
                              </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

<div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
  <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
    Wins / Losses by Exit Strategy
  </div>
  <ResponsiveContainer width="100%" height={300}>
    <BarChart
      data={winsLossByExit.map((item) => ({
        ...item,
        strategy: item.strategy?.trim() || "Unknown"
      }))}
      layout="vertical"
      margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
      <YAxis
        type="category"
        dataKey="strategy"
        width={110}
        stroke={isDark ? "#94a3b8" : "#6b7280"}
        axisLine={false} 
        tickLine={false}
        tickFormatter={truncateStrategy}
      />
      <XAxis
        type="number"
        allowDecimals={false}
        stroke={isDark ? "#94a3b8" : "#6b7280"}
        axisLine={false} 
        tickLine={false}
      />
      <Tooltip
        contentStyle={{
          background: isDark ? "#1e293b" : "#fff",
          color: isDark ? "#e0e7ef" : "#111"
        }}
        cursor={{ fill: isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(203, 213, 225, 0.4)" }}
      />
      <Legend formatter={renderLegendText} iconType="circle"  />
      <Bar dataKey="Wins" stackId="a" fill="#2563eb" barSize={18} radius={[4, 0, 0, 4]} />
      <Bar dataKey="Losses" stackId="a" fill="#FFFF00" barSize={18} radius={[0, 4, 4, 0]}>
        <LabelList content={renderWinPercentageLabel("Wins", "Losses", winsLossByExit)} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
</div>
                </div>


<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
    <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
      Wins / Losses by Timeframe
    </div>
    {totalTfTrades > 0 ? (
      <ResponsiveContainer width="100%" height={420}>
        <BarChart
          data={timeframeAgg}
          layout="vertical"
          margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
          <XAxis type="number" stroke={isDark ? "#94a3b8" : "#6b7280"} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="label"
            stroke={isDark ? "#94a3b8" : "#6b7280"}
            width={120}
            tick={{ fontSize: 14 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<TfCountsTooltip />} cursor={{ fill: isDark ? "rgba(100,116,139,.2)" : "rgba(203,213,225,.4)" }} />
          <Legend formatter={renderLegendText} iconType="circle" wrapperStyle={{ paddingBottom: '10px' }} />
          <Bar dataKey="Wins" fill={POS}  barSize={18} radius={[4, 4, 4, 4]}>
            <LabelList content={renderWinPercentageLabel("Wins", "Losses", timeframeAgg)} />
          </Bar>
          <Bar dataKey="Losses" fill={NEG} barSize={18} radius={[4, 4, 4, 4]} />
        </BarChart>
      </ResponsiveContainer>
    ) : (
      <div className="flex items-center justify-center h-[420px] text-gray-500 dark:text-gray-400">
        No timeframe data available
      </div>
    )}
  </div>

  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
  <div className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-400">
    Average Profit / Loss by Timeframe
  </div>
  
  {/* Legend stays outside the chart */}
  <div className="flex items-center justify-center gap-2 text-xs mb-2 text-gray-600 dark:text-gray-300">
    <FaExclamationTriangle className="text-red-500 text-sm" /> 
    <span>Average Loss is larger than Average Win (Negative R:R)</span>
  </div>

  {hasTfAverages ? (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart
        data={timeframeAgg}
        layout="vertical"
        /* Increased left margin to 25 to prevent the icon from getting cut off */
        margin={{ top: 10, right: 30, left: 25, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
        <XAxis
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(v) => isPrivacyMode ? "***" : formatterCurrency.format(v)}
          stroke={isDark ? "#94a3b8" : "#6b7280"}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          stroke={isDark ? "#94a3b8" : "#6b7280"}
          width={120}
          tick={{ fontSize: 14 }}
          axisLine={false}
          tickLine={false}
        />
        <ReferenceLine x={0} stroke={isDark ? "#94a3b8" : "#9ca3af"} strokeDasharray="3 3" />
        <Tooltip content={<TfAvgTooltip />} cursor={{ fill: isDark ? "rgba(100,116,139,.2)" : "rgba(203,213,225,.4)" }} />
        <Legend
          iconType="circle"
          wrapperStyle={{ paddingBottom: '10px' }}
          formatter={renderLegendText}
        />

        <Bar dataKey="AvgWin" name="Avg Wins" barSize={18} fill={POS} radius={[4, 4, 4, 4]}>
          {timeframeAgg.map((e, i) => (
            <Cell key={`aw-${i}`} fill={e.AvgWin < 0 ? NEG : POS} />
          ))}
        </Bar>

        {/* The updated Bar with the LabelList is now correctly inside the BarChart */}
        <Bar dataKey="AvgLoss" name="Avg Losses" barSize={18} fill={NEG} radius={[4, 4, 4, 4]}>
          {timeframeAgg.map((e, i) => (
            <Cell key={`al-${i}`} fill={e.AvgLoss < 0 ? NEG : POS} />
          ))}
          <LabelList content={renderRRErrorIndicator("AvgWin", "AvgLoss", timeframeAgg)} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  ) : (
    <div className="flex items-center justify-center h-[420px] text-gray-500 dark:text-gray-400">
      No average P&L data available
    </div>
  )}
</div>
</div>
        </div>


                {hasTickPipData && (
                  <CollapsibleSection title={`Potential vs Actual (${tickPipUnit})`}>
                    {/* 1. THE "SO WHAT?" BANNER */}
                    <div className="mb-6 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/30 p-5">
                      <h3 className="flex items-center gap-2 font-bold text-lg text-blue-900 dark:text-blue-300 mb-2">
                        <FaLightbulb className="text-yellow-500" />
                        Trade Management Analysis
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {avgPotentialWin > 0 ? (
                          avgPotentialWin > avgTicksWin && (avgPotentialWin - avgTicksWin) > (avgPotentialWin * 0.2) ? (
                            <>
                              You are capturing an average of <strong>{avgTicksWin?.toFixed(1)} {tickPipUnit}</strong> on winning trades, but your setups have a potential of <strong>{avgPotentialWin?.toFixed(1)} {tickPipUnit}</strong>.
                              <span className="block mt-2 font-semibold text-yellow-700 dark:text-yellow-400">Action: You are leaving money on the table. Try trailing your stop loss instead of taking full profit at your first target to capture the rest of the move.</span>
                            </>
                          ) : (
                            <>
                              You are capturing <strong>{avgTicksWin?.toFixed(1)} {tickPipUnit}</strong> on average, which closely matches your setups' potential. You are highly efficient at taking the meat of the move.
                              <span className="block mt-2 font-semibold text-blue-800 dark:text-blue-400">Action: Keep your take-profit rules exactly as they are. Do not let greed force you to hold too long.</span>
                            </>
                          )
                        ) : (
                          <>Log your "Potential" movement on your winning trades to see if you are exiting your setups too early.</>
                        )}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                      <div className="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6">
                        <div className="flex items-center mb-4">
                          <div className="text-xl font-bold text-blue-900 dark:text-blue-400">
                            Avg Actual vs. Potential {tickPipUnit} by Time
                          </div>
                          <ChartInfoTooltip 
                            text={`Compares what you actually captured (Actual) vs what was available (Potential).\n\nWins (Blue/Green):\nHow much of the move you caught. An average bigger potential vs actual indicates you are exiting trades too soon.\n\nLosses (Yellow/Red):\n"Actual" is your loss size. "Potential" on a loss usually indicates the move you missed, you potentially have gotten in too soon or didn't have a big enough stop loss.`} 
                          />
                        </div>

                        <div className="h-[360px] flex flex-col">
                          {potentialVsActualByTime.some(d => 
                             d["Win Actual"] !== 0 || d["Win Potential"] !== 0 || 
                             d["Loss Actual"] !== 0 || d["Loss Potential"] !== 0
                          ) ? (
                            <>
                              {/* UPDATED LEGEND */}
                                    <div className="flex flex-wrap items-center justify-center gap-6 text-xs mb-2 text-gray-600 dark:text-gray-300">
                                      <span className="flex items-center gap-1.5">
                                        <FaStar className="text-amber-500 text-sm" /> 
                                        Potential &gt; Actual Win (Maybe exiting trades too soon)
                                      </span>
                                      <span className="flex items-center gap-1.5">
                                        <FaExclamationTriangle className="text-red-500 text-sm" /> 
                                        Potential &gt; Actual Loss (Possibily SL too tight or entered early)
                                      </span>
                                    </div>

                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={potentialVsActualByTime} margin={{ top: 25, right: 30, left: 0, bottom: 0 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                                  <XAxis dataKey="name" stroke={isDark ? "#94a3b8" : "#6b7280"} tick={{ fontSize: 12 }} interval={0} angle={-45} textAnchor="end" height={60} />
                                  <YAxis stroke={isDark ? "#94a3b8" : "#6b7280"} />
                                  <Tooltip content={<PotVsActTimeTooltip />} cursor={{ fill: isDark ? "rgba(100,116,139,.2)" : "rgba(203,213,225,.4)" }} />
                                  <Legend formatter={renderLegendText} wrapperStyle={{ paddingBottom: '10px' }} />
                                  <ReferenceLine y={0} stroke={isDark ? "#94a3b8" : "#9ca3af"} />

                                  <Bar dataKey="Win Actual" fill="#2563eb" name={`Avg ${tickPipUnit} (Wins)`} radius={[4, 4, 0, 0]} />
                                  <Bar dataKey="Win Potential" fill="#22c55e" name={`Avg Potential (Wins)`} radius={[4, 4, 0, 0]}>
                                     <LabelList content={renderPotentialIndicator("Win Actual", "Win Potential", potentialVsActualByTime, false)} />
                                  </Bar>

                                  <Bar dataKey="Loss Actual" fill="#eab308" name={`Avg ${tickPipUnit} (Losses)`} radius={[0, 0, 4, 4]} />
                                  <Bar dataKey="Loss Potential" fill="#ef4444" name={`Avg Potential (Losses)`} radius={[4, 4, 0, 0]}>
                                     <LabelList content={renderPotentialIndicator("Loss Actual", "Loss Potential", potentialVsActualByTime, true)} />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                              No data available for comparison
                            </div>
                          )}
                        </div>

                      </div>
                    </div>
                  </CollapsibleSection>
                )}


      <div id="summarytable" className="mt-8">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-lg font-bold text-blue-900 dark:text-blue-400">
              Summary Table
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <TradingCalendar 
              journalRows={calendarRowsSafe} 
              onDayClick={handleDayClick} 
              isPrivacyMode={isPrivacyMode} 
            />
            
            <table className="w-full text-xs text-left">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-3 py-2 font-semibold tracking-wider">Product</th>
                  <th className="px-3 py-2 font-semibold tracking-wider text-right">Size</th>
                  <th className="px-3 py-2 font-semibold tracking-wider">Date</th>
                  <th className="px-3 py-2 font-semibold tracking-wider">Entry</th>
                  <th className="px-3 py-2 font-semibold tracking-wider">Exit</th>
                  <th className="px-3 py-2 font-semibold tracking-wider text-right">Duration</th>
                  <th className="px-3 py-2 font-semibold tracking-wider text-right">Reward</th>
                  <th className="px-3 py-2 font-semibold tracking-wider text-right">Position</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {currentRows.map((r, i) => {
                  const rawReward = parseFloat(String(r["Actual Reward"]).replace(/[^0-9.-]+/g,""));
                  const isProfit = !isNaN(rawReward) && rawReward >= 0;
                  const isLoss = !isNaN(rawReward) && rawReward < 0;

                  return (
                    <tr 
                      key={r.id ?? i} 
                      className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {shortProduct(r["Product"])}
                      </td>
                      <td className="px-3 py-2 text-right  text-gray-600 dark:text-gray-300">
                        {r["Suggested Max # of Contracts"] || r["Size"] || "-"}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDateUS(r["Date"])}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatTimeWithAmPm(toAmPm(r["Entry Time"]))}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatTimeWithAmPm(toAmPm(r["Exit Time"]))}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-mono">
                        {prettyDuration(getAndRecalculateDuration(r))}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-medium ${
                          isProfit ? "text-emerald-600 dark:text-emerald-400" : 
                          isLoss ? "text-rose-600 dark:text-rose-400" : 
                          "text-gray-500"
                      }`}>
                        {isPrivacyMode ? "******" : formatCurrency(r["Actual Reward"])}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r["Position"] && r["Position"] !== "-" && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              isBuyPos(r["Position"])
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-400"
                                : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                            }`}
                          >
                            {r["Position"]}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                Page <span className="font-semibold text-gray-900 dark:text-white">{currentPage}</span> of <span className="font-semibold text-gray-900 dark:text-white">{totalPages}</span>
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-[11px] font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-all"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-[11px] font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>


            <div id="ai-feedback" className="mt-8">
              <AiFeedbackModule
                userId={userId}
                accountId={accountId}
                stats={{
                  winPct,
                  tradeCount,
                  lossCount,
                  overallIncrease,
                  overallPct,
                  todayInc,
                  todayPct,
                  todayCount,
                  weekInc,
                  weekPct,
                  bestProduct,
                  worstProduct,
                  averageRiskToReward,
                  averageWinTrade,
                  averageLosingTrade,
                  yesRules,
                  noRules,
                  winRateWhenRules,
                  lossWhenRules,
                  bestDay,
                  worstDay,
                  bestDayPct,
                  bestDayPctDate,
                  buyCount,
                  buyWinCount,
                  sellCount,
                  sellWinCount,
                  moodTradeCount,
                  posMood12Count,
                  neutralMood3Count,
                  negMood45Count,
                  winRatePositive,
                  winRateNeutral,
                  winRateNegative,
                  quickSuccessionTrades,
                  bestTrade,
                  worstTrade,
                  longestTrade,
                  shortestTrade,
                  winsLossByTimeframe,
                  timeframeAgg,
                  durationAnalysisData,
                }}
                filteredRows={filteredRows}
                activeFilters={filters}
              />
            </div>
  </>
)}
</div> 
      {selectedDate && (
        <DayDetailModal
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
        />
      )}

             {/* Floating Calculator */}
      <button onClick={() => setShowCalculator(true)} className="fixed bottom-12 right-6 z-40 bg-gray-800 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
        <FaCalculator />
      </button>
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
</div> 

);
}